export type GlossaryCategory =
  | "Production Performance"
  | "Manufacturing Systems"
  | "Industrial IoT & Automation"
  | "Energy Management"
  | "Quality Management"
  | "Maintenance & Reliability"
  | "Planning & Traceability"
  | "Lean Manufacturing"
  | "Industrial Hardware";

export const GLOSSARY_CATEGORIES: GlossaryCategory[] = [
  "Production Performance",
  "Manufacturing Systems",
  "Industrial IoT & Automation",
  "Energy Management",
  "Quality Management",
  "Maintenance & Reliability",
  "Planning & Traceability",
  "Lean Manufacturing",
  "Industrial Hardware",
];

export type GlossaryEntry = {
  slug: string;
  term: string;
  fullName?: string;
  acronym?: string;
  category: GlossaryCategory;
  shortDefinition: string;
  definition: string;
  formula?: string;
  whyItMatters: string;
  relatedTerms: string[];
  searchKeywords: string[];
  aliases?: string[];
  /** @deprecated legacy field kept for MCP compatibility */
  short?: string;
};

const e = (x: GlossaryEntry): GlossaryEntry => ({ ...x, short: x.shortDefinition });

export const glossary: GlossaryEntry[] = [
  // ============ Production Performance ============
  e({
    slug: "oee",
    term: "OEE",
    fullName: "Overall Equipment Effectiveness",
    acronym: "OEE",
    aliases: ["Overall Equipment Effectiveness"],
    category: "Production Performance",
    shortDefinition: "Composite KPI of Availability × Performance × Quality that measures how effectively equipment produces good units during planned production time.",
    definition: "OEE is the standard benchmark for equipment productivity. It combines three factors: Availability (uptime versus planned production time), Performance (actual speed versus ideal speed) and Quality (good units versus total units). World-class OEE is typically 85%. In BEEDIGIT, OEE is computed in real time from machine signals anchored to job orders, shifts and work centers, so losses become visible while a shift is still running.",
    formula: "OEE = Availability × Performance × Quality",
    whyItMatters: "OEE turns dozens of losses into one comparable score across machines, shifts and sites — the fastest way to see whether improvement actions actually moved the needle.",
    relatedTerms: ["oee-availability", "oee-performance", "oee-quality", "teep", "six-big-losses"],
    searchKeywords: ["oee", "overall equipment effectiveness", "productivity", "kpi"],
  }),
  e({
    slug: "oee-availability",
    term: "OEE Availability",
    category: "Production Performance",
    shortDefinition: "Share of planned production time the equipment was actually running.",
    definition: "Availability is the first OEE factor. It compares Run Time to Planned Production Time and captures every stop long enough to matter — breakdowns, changeovers, material starvation, missing operators. Planned downtime such as scheduled maintenance is usually excluded from planned production time so it does not distort the loss picture.",
    formula: "Availability = Run Time / Planned Production Time",
    whyItMatters: "Availability is where most factories find their biggest, cheapest wins — reducing unplanned stops and changeovers directly recovers hours of production per week.",
    relatedTerms: ["oee", "downtime", "changeover-time", "planned-production-time"],
    searchKeywords: ["availability", "uptime", "downtime loss"],
  }),
  e({
    slug: "oee-performance",
    term: "OEE Performance",
    category: "Production Performance",
    shortDefinition: "How close actual output ran to the ideal cycle time while the machine was up.",
    definition: "Performance is the second OEE factor. It compares actual production speed to the ideal cycle time defined for the product. It captures small stops, micro-stops and reduced speed — losses that rarely appear in shift reports but silently erode throughput. Accurate Performance requires a trustworthy ideal cycle time per SKU.",
    formula: "Performance = (Ideal Cycle Time × Total Count) / Run Time",
    whyItMatters: "Performance losses are invisible without real-time monitoring; they are the classic 'hidden factory' that appears once machines report cycles automatically.",
    relatedTerms: ["oee", "ideal-cycle-time", "actual-cycle-time", "micro-stop"],
    searchKeywords: ["performance", "speed loss", "hidden factory"],
  }),
  e({
    slug: "oee-quality",
    term: "OEE Quality",
    category: "Production Performance",
    shortDefinition: "Share of produced units that met specification the first time.",
    definition: "Quality is the third OEE factor. It compares good units to total units produced, penalising scrap and rework. It aligns OEE with what actually leaves the plant — sellable product — rather than just what was made.",
    formula: "Quality = Good Count / Total Count",
    whyItMatters: "A machine can look busy and fast yet destroy value if scrap climbs; Quality keeps OEE honest and ties the shop floor to customer commitments.",
    relatedTerms: ["oee", "first-pass-yield", "scrap", "rework"],
    searchKeywords: ["quality", "scrap", "yield"],
  }),
  e({
    slug: "teep",
    term: "TEEP",
    fullName: "Total Effective Equipment Performance",
    acronym: "TEEP",
    aliases: ["Total Effective Equipment Performance"],
    category: "Production Performance",
    shortDefinition: "OEE measured against all calendar time, not just planned production time.",
    definition: "TEEP extends OEE by using all available time (24×7) as the denominator instead of Planned Production Time. It exposes capacity locked up in unused shifts, weekends and holidays, and is used when leadership evaluates whether to add capacity or add a shift.",
    formula: "TEEP = OEE × Utilization (Planned Production Time / All Time)",
    whyItMatters: "TEEP answers a capex question OEE cannot: 'Do we buy another machine, or unlock the one we already own?'",
    relatedTerms: ["oee", "ooe", "all-time", "capacity-utilization"],
    searchKeywords: ["teep", "utilization", "capacity"],
  }),
  e({
    slug: "ooe",
    term: "OOE",
    fullName: "Overall Operations Effectiveness",
    acronym: "OOE",
    aliases: ["Overall Operations Effectiveness"],
    category: "Production Performance",
    shortDefinition: "OEE variant using operating time (including planned stops) as the denominator.",
    definition: "OOE sits between OEE and TEEP. It counts planned downtime such as maintenance and changeovers against the equipment, giving a stricter view of operational effectiveness than OEE while still ignoring unstaffed hours.",
    formula: "OOE = Availability (vs Operating Time) × Performance × Quality",
    whyItMatters: "OOE prevents teams from 'planning away' losses — if a stop is scheduled every week, OOE still counts it.",
    relatedTerms: ["oee", "teep", "planned-downtime"],
    searchKeywords: ["ooe", "operations effectiveness"],
  }),
  e({
    slug: "uptime",
    term: "Uptime",
    category: "Production Performance",
    shortDefinition: "Time a machine was available and running.",
    definition: "Uptime is the total time equipment was operating and producing. It is the positive counterpart to downtime and feeds directly into Availability. In BEEDIGIT it is derived automatically from machine signals rather than shift-end paperwork.",
    whyItMatters: "Uptime tracked in real time exposes silent losses long before they show up in monthly reports.",
    relatedTerms: ["downtime", "oee-availability", "mtbf"],
    searchKeywords: ["uptime", "run time"],
  }),
  e({
    slug: "downtime",
    term: "Downtime",
    category: "Production Performance",
    shortDefinition: "Any period a machine is not producing at target speed.",
    definition: "Downtime is classified into planned (changeovers, maintenance, breaks) and unplanned (breakdowns, material starvation, quality holds). BEEDIGIT captures stops automatically from machine signals and prompts operators to assign a reason code, feeding Pareto analyses that drive continuous improvement.",
    whyItMatters: "Categorised downtime turns 'the line was slow today' into a ranked list of causes engineering can actually attack.",
    relatedTerms: ["planned-downtime", "unplanned-downtime", "oee-availability", "andon"],
    searchKeywords: ["downtime", "stops", "outage"],
  }),
  e({
    slug: "planned-downtime",
    term: "Planned Downtime",
    category: "Production Performance",
    shortDefinition: "Stops scheduled in advance, such as PM, changeovers or breaks.",
    definition: "Planned downtime is time the plant has decided not to produce. It includes preventive maintenance, product changeovers, meetings, breaks and no-demand periods. It is typically excluded from Planned Production Time in the OEE calculation, but tracked separately for scheduling.",
    whyItMatters: "Even planned stops erode capacity; measuring them reveals changeover-reduction and SMED opportunities.",
    relatedTerms: ["unplanned-downtime", "changeover-time", "smed", "planned-production-time"],
    searchKeywords: ["planned downtime", "scheduled stops"],
  }),
  e({
    slug: "unplanned-downtime",
    term: "Unplanned Downtime",
    category: "Production Performance",
    shortDefinition: "Unexpected stops caused by breakdowns, starvation or quality holds.",
    definition: "Unplanned downtime is any stop that was not on the schedule — equipment failure, missing material, operator absence, upstream blockage. It is the single largest Availability loss in most factories and the primary target of predictive maintenance and Andon programs.",
    whyItMatters: "Reducing unplanned downtime lifts OEE the fastest because every hour recovered is pure incremental output.",
    relatedTerms: ["downtime", "breakdown", "mtbf", "andon"],
    searchKeywords: ["unplanned downtime", "breakdown"],
  }),
  e({
    slug: "availability-loss",
    term: "Availability Loss",
    category: "Production Performance",
    shortDefinition: "Time lost to stops during planned production.",
    definition: "Availability Loss is production time lost to any stop long enough to require a reason code — typically breakdowns and changeovers. It is the gap between Planned Production Time and Run Time.",
    whyItMatters: "Availability losses are the first place OEE improvement projects should look.",
    relatedTerms: ["oee-availability", "downtime", "six-big-losses"],
    searchKeywords: ["availability loss", "stop loss"],
  }),
  e({
    slug: "performance-loss",
    term: "Performance Loss",
    category: "Production Performance",
    shortDefinition: "Speed lost to micro-stops and reduced running speed.",
    definition: "Performance Loss is the gap between Ideal Run Rate and Actual Run Rate while the machine is up. Causes include minor jams, sensor issues, worn tooling and deliberate slowdowns by operators.",
    whyItMatters: "Performance losses are the 'hidden factory' — only automatic cycle counting exposes them reliably.",
    relatedTerms: ["oee-performance", "micro-stop", "ideal-run-rate"],
    searchKeywords: ["performance loss", "speed loss"],
  }),
  e({
    slug: "quality-loss",
    term: "Quality Loss",
    category: "Production Performance",
    shortDefinition: "Output lost to scrap and rework.",
    definition: "Quality Loss is production that did not meet specification the first time — either scrapped or reworked. It penalises OEE and consumes materials, energy and machine hours that produced no sellable output.",
    whyItMatters: "A defect made at high speed still costs materials and capacity; Quality Loss quantifies both.",
    relatedTerms: ["oee-quality", "scrap", "rework", "first-pass-yield"],
    searchKeywords: ["quality loss", "defects"],
  }),
  e({
    slug: "six-big-losses",
    term: "Six Big Losses",
    category: "Production Performance",
    shortDefinition: "The canonical taxonomy of losses that erode OEE.",
    definition: "The Six Big Losses framework groups OEE losses into: breakdowns and setup/adjustments (Availability); small stops and reduced speed (Performance); startup rejects and production rejects (Quality). Assigning every stop to one of the six categories creates a shared language between operations, engineering and management.",
    whyItMatters: "It converts vague 'the line is bad' complaints into a ranked, actionable improvement plan.",
    relatedTerms: ["oee", "availability-loss", "performance-loss", "quality-loss"],
    searchKeywords: ["six big losses", "tpm losses"],
  }),
  e({
    slug: "cycle-time",
    term: "Cycle Time",
    category: "Production Performance",
    shortDefinition: "Time to produce one unit from start to finish.",
    definition: "Cycle Time is the interval between two successive units coming off the same station or machine. It differs from Takt Time (customer demand rhythm) and Lead Time (order-to-delivery). Automated cycle counting is the foundation of Performance in OEE.",
    whyItMatters: "You cannot balance a line or size buffers without a reliable cycle time per SKU per work center.",
    relatedTerms: ["actual-cycle-time", "ideal-cycle-time", "takt-time", "lead-time"],
    searchKeywords: ["cycle time"],
  }),
  e({
    slug: "actual-cycle-time",
    term: "Actual Cycle Time",
    category: "Production Performance",
    shortDefinition: "Real, observed time per unit including slowdowns.",
    definition: "Actual Cycle Time is the measured time to produce one unit during real operation. It includes all micro-stops and speed losses and is compared to Ideal Cycle Time to compute Performance.",
    whyItMatters: "The gap between actual and ideal cycle time is where Performance losses live.",
    relatedTerms: ["cycle-time", "ideal-cycle-time", "oee-performance"],
    searchKeywords: ["actual cycle time"],
  }),
  e({
    slug: "ideal-cycle-time",
    term: "Ideal Cycle Time",
    category: "Production Performance",
    shortDefinition: "Fastest sustainable cycle time the machine can achieve for a given product.",
    definition: "Ideal Cycle Time is the theoretical minimum time per unit at design speed for a specific SKU. It is the reference against which actual performance is measured and must be maintained per product to keep OEE credible.",
    whyItMatters: "A wrong ideal cycle time silently distorts every OEE score downstream.",
    relatedTerms: ["cycle-time", "actual-cycle-time", "ideal-run-rate"],
    searchKeywords: ["ideal cycle time", "nameplate speed"],
  }),
  e({
    slug: "takt-time",
    term: "Takt Time",
    category: "Production Performance",
    shortDefinition: "Available production time divided by customer demand.",
    definition: "Takt Time is the pace at which a line must produce to meet customer demand. Unlike cycle time, it is set by the market, not the machine. Comparing Takt Time to Cycle Time reveals whether a line is balanced or a bottleneck.",
    formula: "Takt Time = Available Time / Customer Demand",
    whyItMatters: "Takt Time is the anchor for line balancing, staffing and pull-production design.",
    relatedTerms: ["cycle-time", "bottleneck", "pull-production"],
    searchKeywords: ["takt time", "customer demand rhythm"],
  }),
  e({
    slug: "lead-time",
    term: "Lead Time",
    category: "Production Performance",
    shortDefinition: "Total elapsed time from order to delivery.",
    definition: "Lead Time is the end-to-end time a customer or downstream process waits for a product or batch. It spans queue, setup, run and move times. Shortening lead time is a core Lean objective.",
    whyItMatters: "Short lead times reduce WIP, cash tied up in inventory and the risk of last-minute plan changes.",
    relatedTerms: ["cycle-time", "wip", "otif", "production-lead-time"],
    searchKeywords: ["lead time"],
  }),
  e({
    slug: "run-time",
    term: "Run Time",
    category: "Production Performance",
    shortDefinition: "Time equipment was actually producing.",
    definition: "Run Time equals Planned Production Time minus Availability Losses. It is the denominator for the Performance calculation in OEE.",
    whyItMatters: "Run Time separates 'the line was scheduled' from 'the line was working'.",
    relatedTerms: ["planned-production-time", "oee-availability"],
    searchKeywords: ["run time"],
  }),
  e({
    slug: "planned-production-time",
    term: "Planned Production Time",
    category: "Production Performance",
    shortDefinition: "Time the plant intended the equipment to produce.",
    definition: "Planned Production Time is All Time minus scheduled non-production periods such as no-demand, no-staff, meetings and planned maintenance. It is the denominator of OEE Availability.",
    whyItMatters: "Defining it consistently across sites is the first requirement for comparable OEE benchmarks.",
    relatedTerms: ["all-time", "oee-availability", "planned-downtime"],
    searchKeywords: ["planned production time"],
  }),
  e({
    slug: "net-run-time",
    term: "Net Run Time",
    category: "Production Performance",
    shortDefinition: "Run Time minus Performance losses.",
    definition: "Net Run Time is the time it would have taken to produce the actual output at ideal cycle time. Comparing it to Run Time exposes speed and micro-stop losses.",
    whyItMatters: "Net Run Time is the internal step in the OEE formula that isolates Performance.",
    relatedTerms: ["run-time", "oee-performance", "fully-productive-time"],
    searchKeywords: ["net run time"],
  }),
  e({
    slug: "fully-productive-time",
    term: "Fully Productive Time",
    category: "Production Performance",
    shortDefinition: "Time that produced only good units at ideal speed.",
    definition: "Fully Productive Time is the portion of Planned Production Time that delivered good product at design speed. Dividing it by Planned Production Time gives OEE directly.",
    formula: "OEE = Fully Productive Time / Planned Production Time",
    whyItMatters: "It is the single number that best represents true value-adding equipment time.",
    relatedTerms: ["oee", "net-run-time", "planned-production-time"],
    searchKeywords: ["fully productive time"],
  }),
  e({
    slug: "actual-run-rate",
    term: "Actual Run Rate",
    category: "Production Performance",
    shortDefinition: "Real production speed observed on the line.",
    definition: "Actual Run Rate is the measured output per hour during Run Time. It is compared to Ideal Run Rate to compute Performance in OEE.",
    whyItMatters: "Automatic run-rate measurement is what makes Performance losses visible.",
    relatedTerms: ["ideal-run-rate", "oee-performance", "actual-cycle-time"],
    searchKeywords: ["actual run rate"],
  }),
  e({
    slug: "ideal-run-rate",
    term: "Ideal Run Rate",
    category: "Production Performance",
    shortDefinition: "Design or nameplate production speed of the equipment for a given SKU.",
    definition: "Ideal Run Rate is the fastest sustainable output the machine can deliver for a specific product. It is the reference for both Performance and capacity planning.",
    whyItMatters: "Realistic ideal run rates prevent both false-good and false-bad OEE readings.",
    relatedTerms: ["actual-run-rate", "ideal-cycle-time", "capacity"],
    searchKeywords: ["ideal run rate", "nameplate"],
  }),
  e({
    slug: "throughput",
    term: "Throughput",
    category: "Production Performance",
    shortDefinition: "Rate at which good units leave a process.",
    definition: "Throughput is the average number of good units produced per unit of time, usually measured at the bottleneck. It combines speed, uptime and yield into a single business-facing number.",
    whyItMatters: "Throughput at the constraint is what actually earns revenue.",
    relatedTerms: ["production-output", "bottleneck", "oee"],
    searchKeywords: ["throughput"],
  }),
  e({
    slug: "production-output",
    term: "Production Output",
    category: "Production Performance",
    shortDefinition: "Total units produced in a period.",
    definition: "Production Output is the count of units (or tons, meters, litres) produced during a shift, day or campaign. It is the raw input to yield, OEE and schedule attainment calculations.",
    whyItMatters: "Real-time output tracking replaces end-of-shift paperwork with live visibility.",
    relatedTerms: ["throughput", "schedule-attainment", "yield"],
    searchKeywords: ["output", "production count"],
  }),
  e({
    slug: "capacity",
    term: "Capacity",
    category: "Production Performance",
    shortDefinition: "Maximum output a resource can sustainably deliver.",
    definition: "Capacity is the theoretical or demonstrated maximum output of a machine, line or plant over a defined period. It sets the upper bound on planning and drives investment decisions.",
    whyItMatters: "Confusing installed capacity with effective capacity leads to broken promises to customers.",
    relatedTerms: ["capacity-utilization", "teep", "bottleneck"],
    searchKeywords: ["capacity"],
  }),
  e({
    slug: "capacity-utilization",
    term: "Capacity Utilization",
    category: "Production Performance",
    shortDefinition: "Share of available capacity that was actually used.",
    definition: "Capacity Utilization compares actual output to maximum possible output over a period. It highlights unused calendar time and is a core input to TEEP.",
    formula: "Utilization = Actual Output / Maximum Possible Output",
    whyItMatters: "Low utilization is often mistaken for a machine problem when it is really a demand or scheduling problem.",
    relatedTerms: ["capacity", "teep", "all-time"],
    searchKeywords: ["capacity utilization"],
  }),
  e({
    slug: "schedule-attainment",
    term: "Schedule Attainment",
    category: "Production Performance",
    shortDefinition: "Share of scheduled production actually completed on time.",
    definition: "Schedule Attainment measures whether the plant produced the right products in the right quantities during the scheduled window. Together with OEE, it separates 'we ran fast' from 'we ran the right things'.",
    whyItMatters: "High OEE with low schedule attainment usually means the plant is making easy products, not needed ones.",
    relatedTerms: ["oee", "otif", "production-scheduling"],
    searchKeywords: ["schedule attainment", "adherence"],
  }),
  e({
    slug: "first-pass-yield",
    term: "First Pass Yield",
    fullName: "First Pass Yield",
    acronym: "FPY",
    aliases: ["FPY"],
    category: "Production Performance",
    shortDefinition: "Share of units that meet specification the first time, without rework.",
    definition: "FPY is the ratio of units passing all quality checks on the first attempt to units started. Unlike overall yield, it penalises rework and reveals process instability that inspection alone can hide.",
    formula: "FPY = Units Passed First Time / Units Started",
    whyItMatters: "Rework is invisible in yield but expensive in labour, materials and lead time; FPY makes it visible.",
    relatedTerms: ["yield", "rework", "scrap", "oee-quality"],
    searchKeywords: ["fpy", "first pass yield", "quality"],
  }),
  e({
    slug: "yield",
    term: "Yield",
    category: "Production Performance",
    shortDefinition: "Share of good units produced from units started.",
    definition: "Yield is the overall ratio of good product to input, including reworked units. It is the baseline quality metric and drives material cost, capacity and customer delivery.",
    formula: "Yield = Good Units / Total Units Started",
    whyItMatters: "Every yield point recovered pays back in materials, energy and machine hours already spent.",
    relatedTerms: ["first-pass-yield", "scrap", "reject-rate"],
    searchKeywords: ["yield"],
  }),
  e({
    slug: "reject-rate",
    term: "Reject Rate",
    category: "Production Performance",
    shortDefinition: "Share of units rejected by quality inspection.",
    definition: "Reject Rate is the proportion of produced units that failed inspection and were scrapped or held for rework. Tracked per SKU, machine and shift, it exposes systemic quality problems.",
    whyItMatters: "Rejects tie up capacity, energy and materials; tracking the rate per machine finds the true source.",
    relatedTerms: ["scrap", "rework", "defect-rate", "yield"],
    searchKeywords: ["reject rate"],
  }),
  e({
    slug: "scrap",
    term: "Scrap",
    category: "Production Performance",
    shortDefinition: "Material or product discarded because it cannot be sold or reworked.",
    definition: "Scrap is production waste that cannot be recovered as sellable product. It is a Quality Loss in OEE and a direct hit to material cost. Reason-coded scrap capture at the machine is essential for root-cause analysis.",
    whyItMatters: "Scrap costs are usually understated because paperwork misses small daily losses; automatic capture fixes that.",
    relatedTerms: ["rework", "reject-rate", "oee-quality"],
    searchKeywords: ["scrap", "waste"],
  }),
  e({
    slug: "rework",
    term: "Rework",
    category: "Production Performance",
    shortDefinition: "Units that failed first inspection but were reprocessed into good product.",
    definition: "Rework is remedial processing of non-conforming units to make them acceptable. It consumes labour, machine time and materials without adding new output, and inflates apparent yield if not tracked separately.",
    whyItMatters: "Chronic rework is a signal of process instability that FPY and SPC can pinpoint.",
    relatedTerms: ["first-pass-yield", "scrap", "spc"],
    searchKeywords: ["rework"],
  }),
  e({
    slug: "bottleneck",
    term: "Bottleneck",
    category: "Production Performance",
    shortDefinition: "The resource that limits overall throughput of a line or plant.",
    definition: "The bottleneck is the slowest step in a process — the constraint that governs total output. According to the Theory of Constraints, improvements anywhere else in the line do not add throughput unless they relieve the bottleneck.",
    whyItMatters: "Focusing improvement on the bottleneck delivers the fastest, cheapest throughput gains.",
    relatedTerms: ["throughput", "takt-time", "capacity"],
    searchKeywords: ["bottleneck", "constraint"],
  }),
  e({
    slug: "changeover-time",
    term: "Changeover Time",
    category: "Production Performance",
    shortDefinition: "Time between the last good unit of one product and the first good unit of the next.",
    definition: "Changeover Time covers cleaning, tooling change, format setup and startup approval when switching SKUs. It is a large Availability loss in high-mix plants and the primary target of SMED.",
    whyItMatters: "Reducing changeover time enables smaller batches, less inventory and faster response to demand.",
    relatedTerms: ["setup-time", "smed", "planned-downtime"],
    searchKeywords: ["changeover", "setup"],
  }),
  e({
    slug: "setup-time",
    term: "Setup Time",
    category: "Production Performance",
    shortDefinition: "Time to prepare equipment before a run can start.",
    definition: "Setup Time is the equipment-preparation portion of a changeover — installing tooling, loading recipes, aligning material. It is measured, standardised and shortened by SMED techniques.",
    whyItMatters: "Setup time drives minimum batch size and thus WIP, cash and responsiveness.",
    relatedTerms: ["changeover-time", "smed"],
    searchKeywords: ["setup time"],
  }),
  e({
    slug: "micro-stop",
    term: "Micro Stop",
    aliases: ["Small Stop"],
    category: "Production Performance",
    shortDefinition: "Short stop, typically under a few minutes, that operators clear themselves.",
    definition: "A micro-stop (or small stop) is a brief interruption — a jam, sensor trip, missing label — that operators reset without calling maintenance. Individually trivial, they aggregate into large Performance losses and are only visible with automatic monitoring.",
    whyItMatters: "Micro-stops are typically the single largest hidden loss on high-speed lines.",
    relatedTerms: ["oee-performance", "performance-loss", "andon"],
    searchKeywords: ["micro stop", "small stop"],
  }),
  e({
    slug: "production-loss",
    term: "Production Loss",
    category: "Production Performance",
    shortDefinition: "Any output the plant should have produced but did not.",
    definition: "Production Loss is the umbrella term for output missed due to Availability, Performance or Quality losses, expressed in units or currency. It converts OEE percentages into money that management can act on.",
    whyItMatters: "Translating losses into currency is the fastest way to prioritise improvement projects.",
    relatedTerms: ["oee", "six-big-losses", "throughput"],
    searchKeywords: ["production loss"],
  }),
  e({
    slug: "wip",
    term: "WIP",
    fullName: "Work in Progress",
    acronym: "WIP",
    aliases: ["Work in Progress"],
    category: "Production Performance",
    shortDefinition: "Partly finished inventory between the start and end of a process.",
    definition: "WIP is material that has entered the process but is not yet finished goods. High WIP hides quality problems, extends lead time and ties up cash. Lean systems deliberately cap WIP to expose issues.",
    whyItMatters: "WIP is the working-capital cost of poor flow; measuring it drives pull-production design.",
    relatedTerms: ["lead-time", "kanban", "pull-production"],
    searchKeywords: ["wip", "work in progress"],
  }),
  e({
    slug: "all-time",
    term: "All Time",
    category: "Production Performance",
    shortDefinition: "Every calendar hour available — the denominator for TEEP.",
    definition: "All Time is 24 hours × 365 days: the total calendar time equipment could theoretically run. Using it as the denominator (instead of Planned Production Time) yields TEEP and exposes unused shifts and weekends.",
    whyItMatters: "All-Time framing is the honest denominator when leadership debates buying more capacity.",
    relatedTerms: ["teep", "planned-production-time", "capacity-utilization"],
    searchKeywords: ["all time", "calendar time"],
  }),

  // ============ Manufacturing Systems ============
  e({
    slug: "mes",
    term: "MES",
    fullName: "Manufacturing Execution System",
    acronym: "MES",
    aliases: ["Manufacturing Execution System"],
    category: "Manufacturing Systems",
    shortDefinition: "Software that connects ERP planning to shop-floor execution.",
    definition: "An MES manages the execution of production orders on the shop floor. It receives job orders from ERP, tracks output, consumption, quality and traceability, then reports confirmations back. Where ERP answers what and when, MES answers how it actually happened. BEEDIGIT is a modular MES built on real-time machine data.",
    whyItMatters: "Without MES, ERP plans and shop-floor reality drift apart within hours of shift start.",
    relatedTerms: ["mom", "erp", "oee", "traceability"],
    searchKeywords: ["mes", "manufacturing execution"],
  }),
  e({
    slug: "mom",
    term: "MOM",
    fullName: "Manufacturing Operations Management",
    acronym: "MOM",
    aliases: ["Manufacturing Operations Management"],
    category: "Manufacturing Systems",
    shortDefinition: "Broader operations layer covering MES plus quality, maintenance and inventory.",
    definition: "MOM (as defined in ISA-95) is the operational layer between ERP and control systems. It groups MES functions with quality, maintenance, inventory and performance analysis into one coordinated stack.",
    whyItMatters: "Thinking in MOM prevents siloed point solutions that cannot share master data or events.",
    relatedTerms: ["mes", "isa-95", "cmms", "qms"],
    searchKeywords: ["mom", "manufacturing operations management"],
  }),
  e({
    slug: "erp",
    term: "ERP",
    fullName: "Enterprise Resource Planning",
    acronym: "ERP",
    aliases: ["Enterprise Resource Planning"],
    category: "Manufacturing Systems",
    shortDefinition: "System of record for orders, inventory, finance and planning.",
    definition: "ERP platforms (SAP, Oracle, Microsoft Dynamics, Odoo, 1C and others) manage the business side of manufacturing: sales orders, purchase orders, inventory, BOMs, finance. BEEDIGIT integrates bi-directionally with ERP — pulling job orders in, and pushing back confirmed output, scrap, downtime and material consumption.",
    whyItMatters: "ERP is the single source of financial truth; MES makes sure the truth it holds matches the floor.",
    relatedTerms: ["mes", "mrp", "mrp-ii", "aps"],
    searchKeywords: ["erp"],
  }),
  e({
    slug: "mrp",
    term: "MRP",
    fullName: "Material Requirements Planning",
    acronym: "MRP",
    aliases: ["Material Requirements Planning"],
    category: "Manufacturing Systems",
    shortDefinition: "Method for calculating what materials to buy and when.",
    definition: "MRP explodes the BOM against demand and inventory to schedule purchase and production orders. It is a core ERP module and the foundation of MRP II and modern APS.",
    whyItMatters: "Reliable MRP output depends on accurate BOMs, routings and shop-floor confirmations.",
    relatedTerms: ["mrp-ii", "erp", "bom", "aps"],
    searchKeywords: ["mrp"],
  }),
  e({
    slug: "mrp-ii",
    term: "MRP II",
    fullName: "Manufacturing Resource Planning",
    acronym: "MRP II",
    aliases: ["Manufacturing Resource Planning"],
    category: "Manufacturing Systems",
    shortDefinition: "Extension of MRP that adds capacity, labour and financial planning.",
    definition: "MRP II expands basic MRP to include work-centre capacity, labour, tooling and financial impact, forming the historical basis of modern ERP.",
    whyItMatters: "Understanding MRP II clarifies which ERP capabilities are planning and which are execution.",
    relatedTerms: ["mrp", "erp", "aps"],
    searchKeywords: ["mrp ii", "manufacturing resource planning"],
  }),
  e({
    slug: "aps",
    term: "APS",
    fullName: "Advanced Planning and Scheduling",
    acronym: "APS",
    aliases: ["Advanced Planning and Scheduling"],
    category: "Manufacturing Systems",
    shortDefinition: "Constraint-based scheduling that considers finite capacity and setup rules.",
    definition: "APS systems create feasible detailed schedules by respecting machine capacity, changeover rules, material availability and skills. They sit between ERP planning and MES execution.",
    whyItMatters: "APS turns idealised MRP plans into shift-level schedules the floor can actually run.",
    relatedTerms: ["mrp", "erp", "finite-capacity-scheduling", "production-scheduling"],
    searchKeywords: ["aps"],
  }),
  e({
    slug: "scada",
    term: "SCADA",
    fullName: "Supervisory Control and Data Acquisition",
    acronym: "SCADA",
    aliases: ["Supervisory Control and Data Acquisition"],
    category: "Manufacturing Systems",
    shortDefinition: "System for supervising and acquiring data from industrial equipment.",
    definition: "SCADA collects real-time data from PLCs, RTUs and sensors, presents it to operators and issues control commands. It is a common upstream source of data for MES and historians.",
    whyItMatters: "SCADA gives operators local control; MES gives the business production context on top of it.",
    relatedTerms: ["plc", "hmi", "dcs", "data-acquisition"],
    searchKeywords: ["scada"],
  }),
  e({
    slug: "plc",
    term: "PLC",
    fullName: "Programmable Logic Controller",
    acronym: "PLC",
    aliases: ["Programmable Logic Controller"],
    category: "Manufacturing Systems",
    shortDefinition: "Industrial controller that runs machine logic in real time.",
    definition: "A PLC is a ruggedised computer designed to control machines and processes with deterministic timing. PLCs are the primary source of machine signals for OEE, downtime and cycle capture.",
    whyItMatters: "Reading directly from PLCs is what makes machine data trustworthy instead of manually reported.",
    relatedTerms: ["scada", "hmi", "opc-ua", "modbus"],
    searchKeywords: ["plc"],
  }),
  e({
    slug: "hmi",
    term: "HMI",
    fullName: "Human-Machine Interface",
    acronym: "HMI",
    aliases: ["Human-Machine Interface"],
    category: "Manufacturing Systems",
    shortDefinition: "Operator screen that displays and controls machine state.",
    definition: "An HMI is the local screen an operator uses to monitor and control a machine. In connected factories, HMI events complement PLC signals to explain what the operator saw and did.",
    whyItMatters: "Combining PLC signals with HMI events removes ambiguity about who did what and when.",
    relatedTerms: ["plc", "scada", "operator-terminal"],
    searchKeywords: ["hmi"],
  }),
  e({
    slug: "dcs",
    term: "DCS",
    fullName: "Distributed Control System",
    acronym: "DCS",
    aliases: ["Distributed Control System"],
    category: "Manufacturing Systems",
    shortDefinition: "Distributed process-control architecture used in continuous plants.",
    definition: "A DCS coordinates many local controllers across a process plant — refineries, chemicals, cement — through a central engineering and operations layer. It is the process-industry counterpart to PLC/SCADA architectures.",
    whyItMatters: "In continuous industries, integrating with the DCS is the only way to get real production context.",
    relatedTerms: ["scada", "plc", "process-industry"],
    searchKeywords: ["dcs"],
  }),
  e({
    slug: "qms",
    term: "QMS",
    fullName: "Quality Management System",
    acronym: "QMS",
    aliases: ["Quality Management System"],
    category: "Manufacturing Systems",
    shortDefinition: "Digital quality control tied to batch genealogy.",
    definition: "A QMS manages inspections, Quality Control Plans, Non-Conformance Reports, 8D and FMEA. Connected to MES data, quality events are linked to the machine, operator, shift and batch that produced them, enabling true root-cause analysis instead of paper trails.",
    whyItMatters: "A QMS wired into MES turns 'quality complaints' into 'quality intelligence'.",
    relatedTerms: ["mes", "spc", "traceability", "iso-9001"],
    searchKeywords: ["qms", "quality system"],
  }),
  e({
    slug: "cmms",
    term: "CMMS",
    fullName: "Computerized Maintenance Management System",
    acronym: "CMMS",
    aliases: ["Computerized Maintenance Management System"],
    category: "Manufacturing Systems",
    shortDefinition: "Software for equipment history, preventive maintenance and work orders.",
    definition: "A CMMS tracks equipment passports, preventive maintenance plans, work requests and spare parts. When connected to production data, maintenance decisions are informed by real downtime patterns and lost output, not just calendar schedules.",
    whyItMatters: "CMMS wired to real machine data replaces guessed PM intervals with condition-driven maintenance.",
    relatedTerms: ["eam", "preventive-maintenance", "mtbf", "cbm"],
    searchKeywords: ["cmms"],
  }),
  e({
    slug: "eam",
    term: "EAM",
    fullName: "Enterprise Asset Management",
    acronym: "EAM",
    aliases: ["Enterprise Asset Management"],
    category: "Manufacturing Systems",
    shortDefinition: "Enterprise-wide management of physical assets across their lifecycle.",
    definition: "EAM extends CMMS to cover the full lifecycle of physical assets — acquisition, operation, maintenance, disposal — usually across multiple sites, with financial and compliance features.",
    whyItMatters: "EAM aligns maintenance decisions with financial depreciation and long-term capacity planning.",
    relatedTerms: ["cmms", "reliability-centered-maintenance"],
    searchKeywords: ["eam"],
  }),
  e({
    slug: "enms",
    term: "EnMS",
    fullName: "Energy Management System",
    acronym: "EnMS",
    aliases: ["Energy Management System"],
    category: "Manufacturing Systems",
    shortDefinition: "ISO 50001 framework for tracking and improving energy performance.",
    definition: "An EnMS establishes baselines, EnPIs, Significant Energy Uses and Specific Energy Consumption. BEEDIGIT links energy meters to production context — orders, batches, shifts — so kWh becomes actionable rather than a lump-sum bill.",
    whyItMatters: "Energy without production context is a bill; with context it becomes a KPI operators can influence.",
    relatedTerms: ["iso-50001", "enpi", "sec", "energy-baseline"],
    searchKeywords: ["enms", "energy management"],
  }),
  e({
    slug: "wms",
    term: "WMS",
    fullName: "Warehouse Management System",
    acronym: "WMS",
    aliases: ["Warehouse Management System"],
    category: "Manufacturing Systems",
    shortDefinition: "Software that manages warehouse operations, locations and picking.",
    definition: "A WMS controls receiving, put-away, picking, packing and shipping in warehouses and finished-goods stores. Integrated with MES, it closes the loop between production output and dispatch.",
    whyItMatters: "WMS + MES prevents the classic 'made it but can't find it' warehouse failure mode.",
    relatedTerms: ["mes", "erp", "traceability"],
    searchKeywords: ["wms"],
  }),
  e({
    slug: "lims",
    term: "LIMS",
    fullName: "Laboratory Information Management System",
    acronym: "LIMS",
    aliases: ["Laboratory Information Management System"],
    category: "Manufacturing Systems",
    shortDefinition: "Software that manages lab samples, tests and results.",
    definition: "A LIMS tracks samples, testing workflows and quality results in labs — critical in food, pharma, chemicals and metals. Integrated with MES, lab results release or hold batches automatically.",
    whyItMatters: "LIMS integration eliminates hours of waiting for paper certificates before a batch can be released.",
    relatedTerms: ["qms", "coa", "traceability"],
    searchKeywords: ["lims"],
  }),
  e({
    slug: "plm",
    term: "PLM",
    fullName: "Product Lifecycle Management",
    acronym: "PLM",
    aliases: ["Product Lifecycle Management"],
    category: "Manufacturing Systems",
    shortDefinition: "System that manages product design data across its lifecycle.",
    definition: "PLM systems manage CAD, specifications, BOMs and change control from concept through end-of-life. They feed engineering BOMs and routings into ERP and MES.",
    whyItMatters: "PLM keeps the shop floor building the version engineering actually released.",
    relatedTerms: ["bom", "erp", "mes"],
    searchKeywords: ["plm"],
  }),
  e({
    slug: "production-data-historian",
    term: "Production Data Historian",
    aliases: ["Data Historian"],
    category: "Manufacturing Systems",
    shortDefinition: "Time-series database optimised for high-frequency industrial data.",
    definition: "A historian stores tag-level machine and process data at high frequency, retaining it for years. It is the raw foundation for OEE calculations, trend analysis and machine learning models.",
    whyItMatters: "Without a historian, sub-second production events are lost the moment they happen.",
    relatedTerms: ["time-series-data", "scada", "edge-computing"],
    searchKeywords: ["historian", "time series"],
  }),
  e({
    slug: "shop-floor-management",
    term: "Shop-Floor Management",
    category: "Manufacturing Systems",
    shortDefinition: "Structured routines that use live production data to run daily operations.",
    definition: "Shop-Floor Management is the daily operating system of a plant — tiered meetings, visual boards, standard work — powered by real-time KPIs. Digital shop-floor management replaces whiteboards with live dashboards fed by MES.",
    whyItMatters: "Even the best data has no impact until it enters a daily management routine.",
    relatedTerms: ["visual-management", "andon", "kaizen"],
    searchKeywords: ["shop floor management", "sfm"],
  }),
  e({
    slug: "production-monitoring",
    term: "Production Monitoring",
    category: "Manufacturing Systems",
    shortDefinition: "Live tracking of machine state, output and losses on the shop floor.",
    definition: "Production Monitoring collects run/stop signals, cycles and reason codes from machines to display OEE, downtime and throughput in real time. It is usually the first module deployed in a digitalization program.",
    whyItMatters: "Monitoring is the entry point: you cannot improve what you cannot see live.",
    relatedTerms: ["oee", "real-time-monitoring", "andon"],
    searchKeywords: ["production monitoring"],
  }),
  e({
    slug: "manufacturing-dashboard",
    term: "Manufacturing Dashboard",
    category: "Manufacturing Systems",
    shortDefinition: "Visual summary of key production KPIs in real time.",
    definition: "A manufacturing dashboard aggregates OEE, output, downtime, quality and energy KPIs for a machine, line, plant or group. Role-specific dashboards serve operators, supervisors, plant managers and executives from the same data.",
    whyItMatters: "A shared dashboard makes performance conversations fact-based rather than opinion-based.",
    relatedTerms: ["production-monitoring", "visual-management", "shop-floor-management"],
    searchKeywords: ["dashboard", "kpi board"],
  }),

  // ============ Industrial IoT & Automation ============
  e({
    slug: "iiot",
    term: "IIoT",
    fullName: "Industrial Internet of Things",
    acronym: "IIoT",
    aliases: ["Industrial Internet of Things"],
    category: "Industrial IoT & Automation",
    shortDefinition: "Connected sensors, machines and analytics applied to industrial processes.",
    definition: "IIoT is the network of industrial devices — sensors, PLCs, gateways — that stream data to platforms where it is combined with production context. It is the technical foundation of modern MES and Industry 4.0 initiatives.",
    whyItMatters: "IIoT is what makes MES continuous rather than a manual data-entry chore.",
    relatedTerms: ["industry-4-0", "edge-computing", "iiot-gateway", "opc-ua"],
    searchKeywords: ["iiot"],
  }),
  e({
    slug: "industry-4-0",
    term: "Industry 4.0",
    category: "Industrial IoT & Automation",
    shortDefinition: "Fourth industrial revolution combining IIoT, data and automation.",
    definition: "Industry 4.0 describes the convergence of IIoT, cloud, analytics, robotics and cyber-physical systems in manufacturing. It is a strategic framing more than a specific technology, aligned with maturity models such as SIRI.",
    whyItMatters: "Industry 4.0 gives leadership a shared vocabulary for prioritising digital investments.",
    relatedTerms: ["iiot", "smart-manufacturing", "siri", "digital-twin"],
    searchKeywords: ["industry 4.0", "i4.0"],
  }),
  e({
    slug: "smart-manufacturing",
    term: "Smart Manufacturing",
    category: "Industrial IoT & Automation",
    shortDefinition: "Data-driven manufacturing where decisions are made from real-time signals.",
    definition: "Smart Manufacturing is the practical application of IIoT, MES and analytics to run operations from live data instead of daily reports. It emphasises visibility, closed-loop control and rapid problem-solving.",
    whyItMatters: "Smart Manufacturing turns Industry 4.0 buzzwords into a shift-by-shift operating model.",
    relatedTerms: ["industry-4-0", "connected-factory", "iiot"],
    searchKeywords: ["smart manufacturing"],
  }),
  e({
    slug: "connected-factory",
    term: "Connected Factory",
    category: "Industrial IoT & Automation",
    shortDefinition: "Plant where machines, systems and people share data in real time.",
    definition: "A Connected Factory has every relevant machine, meter and operator terminal linked to a common platform. Data flows both up (to ERP, BI) and down (schedules, recipes, work instructions).",
    whyItMatters: "Connectivity is the prerequisite for every advanced analytics or AI use case.",
    relatedTerms: ["iiot", "smart-manufacturing", "iiot-gateway"],
    searchKeywords: ["connected factory"],
  }),
  e({
    slug: "ot",
    term: "OT",
    fullName: "Operational Technology",
    acronym: "OT",
    aliases: ["Operational Technology"],
    category: "Industrial IoT & Automation",
    shortDefinition: "Hardware and software that directly monitors or controls physical processes.",
    definition: "OT includes PLCs, SCADA, HMIs, DCS and industrial networks — the systems that run the plant. It differs from IT in reliability, latency and safety requirements.",
    whyItMatters: "Successful digitalization requires respecting OT constraints, not treating machines like servers.",
    relatedTerms: ["it-ot-convergence", "scada", "plc"],
    searchKeywords: ["ot", "operational technology"],
  }),
  e({
    slug: "it-ot-convergence",
    term: "IT/OT Convergence",
    category: "Industrial IoT & Automation",
    shortDefinition: "Integration of enterprise IT systems with shop-floor OT systems.",
    definition: "IT/OT Convergence is the technical and organisational alignment of business IT (ERP, cloud, BI) with plant OT (PLCs, SCADA, MES). It requires shared data models, security policies and clear ownership.",
    whyItMatters: "Most stalled digital programs fail at the IT/OT boundary, not at the technology itself.",
    relatedTerms: ["ot", "isa-95", "unified-namespace"],
    searchKeywords: ["it ot convergence"],
  }),
  e({
    slug: "edge-computing",
    term: "Edge Computing",
    category: "Industrial IoT & Automation",
    shortDefinition: "Processing data close to the source instead of only in the cloud.",
    definition: "Edge Computing runs data collection, buffering and analytics on devices near the machines. It keeps production running when the network is down and reduces cloud bandwidth and latency.",
    whyItMatters: "Edge processing is what lets IIoT platforms survive real factory network conditions.",
    relatedTerms: ["edge-device", "iiot-gateway", "iiot"],
    searchKeywords: ["edge computing"],
  }),
  e({
    slug: "edge-device",
    term: "Edge Device",
    category: "Industrial IoT & Automation",
    shortDefinition: "Ruggedised device that runs edge computing near machines.",
    definition: "An edge device sits next to production equipment, collecting signals from PLCs, sensors and meters, applying local logic and forwarding data to platforms. BEEDIGIT's BEEEDGE and HoneyPlug are examples.",
    whyItMatters: "Edge devices decouple factory operations from cloud availability.",
    relatedTerms: ["edge-computing", "iiot-gateway", "industrial-gateway"],
    searchKeywords: ["edge device"],
  }),
  e({
    slug: "industrial-gateway",
    term: "Industrial Gateway",
    aliases: ["IIoT Gateway"],
    category: "Industrial IoT & Automation",
    shortDefinition: "Hardware that bridges industrial protocols to IIoT platforms.",
    definition: "An industrial gateway (or IIoT gateway) translates protocols such as Modbus, OPC UA or Profinet into modern APIs and message brokers, and forwards data to on-prem or cloud systems.",
    whyItMatters: "Gateways are the pragmatic way to connect brownfield machines without replacing them.",
    relatedTerms: ["iiot", "edge-device", "opc-ua", "modbus"],
    searchKeywords: ["gateway", "iiot gateway"],
  }),
  e({
    slug: "sensor",
    term: "Sensor",
    category: "Industrial IoT & Automation",
    shortDefinition: "Device that measures a physical variable and outputs a signal.",
    definition: "Sensors measure quantities such as temperature, vibration, current, pressure or position. They are the input layer of IIoT and condition-based maintenance programs.",
    whyItMatters: "Sensor choice determines what a plant can actually monitor and predict.",
    relatedTerms: ["smart-sensor", "industrial-sensor", "condition-monitoring"],
    searchKeywords: ["sensor"],
  }),
  e({
    slug: "smart-sensor",
    term: "Smart Sensor",
    category: "Industrial IoT & Automation",
    shortDefinition: "Sensor with built-in processing and digital communication.",
    definition: "A smart sensor combines measurement with on-board processing, calibration and digital output (often IO-Link or MQTT). It reduces wiring and enables self-diagnostics.",
    whyItMatters: "Smart sensors cut integration time and remove entire categories of wiring faults.",
    relatedTerms: ["sensor", "industrial-sensor", "mqtt"],
    searchKeywords: ["smart sensor"],
  }),
  e({
    slug: "machine-connectivity",
    term: "Machine Connectivity",
    category: "Industrial IoT & Automation",
    shortDefinition: "Ability to acquire structured data from a production machine.",
    definition: "Machine Connectivity is the layer that reads signals from a machine — via PLC tags, OPC UA, digital I/O or add-on sensors — and normalises them for MES and analytics.",
    whyItMatters: "It is the practical starting point for OEE monitoring in mixed brownfield plants.",
    relatedTerms: ["machine-data-collection", "opc-ua", "modbus"],
    searchKeywords: ["machine connectivity"],
  }),
  e({
    slug: "machine-data-collection",
    term: "Machine Data Collection",
    aliases: ["Data Acquisition"],
    category: "Industrial IoT & Automation",
    shortDefinition: "Automatic capture of signals from production equipment.",
    definition: "Machine Data Collection (or Data Acquisition) is the continuous, automated recording of machine signals — run/stop, cycles, counters, alarms — for use in OEE, historians and analytics.",
    whyItMatters: "Automated collection eliminates the errors and gaps of manual shift reports.",
    relatedTerms: ["machine-connectivity", "scada", "historian"],
    searchKeywords: ["data acquisition", "machine data"],
  }),
  e({
    slug: "real-time-monitoring",
    term: "Real-Time Monitoring",
    category: "Industrial IoT & Automation",
    shortDefinition: "Continuous live view of equipment and process state.",
    definition: "Real-Time Monitoring provides sub-minute visibility of machine status, cycles, quality and energy so problems are seen and acted on during the shift, not after it.",
    whyItMatters: "Reaction times shrink from days to minutes when monitoring is truly real time.",
    relatedTerms: ["production-monitoring", "andon", "condition-monitoring"],
    searchKeywords: ["real time monitoring"],
  }),
  e({
    slug: "condition-monitoring",
    term: "Condition Monitoring",
    category: "Industrial IoT & Automation",
    shortDefinition: "Tracking equipment health via vibration, temperature and other signals.",
    definition: "Condition Monitoring continuously measures parameters that indicate equipment health — vibration, temperature, current, oil quality — to detect degradation before failure. It is the technical basis for CBM and predictive maintenance.",
    whyItMatters: "It turns maintenance from calendar-based guessing to evidence-based intervention.",
    relatedTerms: ["cbm", "predictive-maintenance", "vibration-monitoring", "thermography"],
    searchKeywords: ["condition monitoring"],
  }),
  e({
    slug: "time-series-data",
    term: "Time-Series Data",
    category: "Industrial IoT & Automation",
    shortDefinition: "Sequence of timestamped measurements from a signal.",
    definition: "Time-series data is measurement points recorded with high-precision timestamps. It is the native format of industrial signals and requires specialised storage such as historians or TSDBs.",
    whyItMatters: "Analytics quality is limited by time-series accuracy and completeness.",
    relatedTerms: ["production-data-historian", "condition-monitoring"],
    searchKeywords: ["time series"],
  }),
  e({
    slug: "opc-ua",
    term: "OPC UA",
    category: "Industrial IoT & Automation",
    shortDefinition: "Modern industrial protocol for secure, structured machine data exchange.",
    definition: "OPC UA is the current standard for interoperable industrial communication. It offers structured information models, security and platform independence, and is widely supported by modern PLCs and edge platforms.",
    whyItMatters: "OPC UA is the safest bet for future-proof machine connectivity.",
    relatedTerms: ["mqtt", "modbus", "iiot", "unified-namespace"],
    searchKeywords: ["opc ua", "opc"],
  }),
  e({
    slug: "mqtt",
    term: "MQTT",
    category: "Industrial IoT & Automation",
    shortDefinition: "Lightweight publish/subscribe protocol widely used in IIoT.",
    definition: "MQTT is a lightweight messaging protocol suited to constrained networks and many small devices. Combined with a broker, it is the backbone of many IIoT and Unified Namespace deployments.",
    whyItMatters: "MQTT decouples devices from applications, enabling scalable event-driven architectures.",
    relatedTerms: ["opc-ua", "unified-namespace", "iiot"],
    searchKeywords: ["mqtt"],
  }),
  e({
    slug: "modbus",
    term: "Modbus",
    category: "Industrial IoT & Automation",
    shortDefinition: "Long-standing industrial protocol for reading and writing device registers.",
    definition: "Modbus (RTU over serial and TCP over Ethernet) is a simple, widely supported protocol for exchanging register values with PLCs, drives and meters. It remains a pragmatic way to connect brownfield equipment.",
    whyItMatters: "Modbus is often the fastest route to first data from older machines.",
    relatedTerms: ["opc-ua", "plc", "industrial-gateway"],
    searchKeywords: ["modbus"],
  }),
  e({
    slug: "rest-api",
    term: "REST API",
    category: "Industrial IoT & Automation",
    shortDefinition: "HTTP-based interface used to integrate manufacturing platforms with IT systems.",
    definition: "A REST API exposes resources over HTTP with standard verbs. In manufacturing digitalization, REST APIs are the common integration surface between MES, ERP, BI and third-party analytics.",
    whyItMatters: "REST APIs are how MES data reaches the systems where business decisions are made.",
    relatedTerms: ["it-ot-convergence", "erp", "mes"],
    searchKeywords: ["rest api"],
  }),
  e({
    slug: "unified-namespace",
    term: "Unified Namespace",
    fullName: "Unified Namespace",
    acronym: "UNS",
    aliases: ["UNS"],
    category: "Industrial IoT & Automation",
    shortDefinition: "Single hierarchical data model where every plant event is published once.",
    definition: "A Unified Namespace (UNS) is a centralised, event-driven data structure — typically over MQTT — where machines, systems and people publish and subscribe to the same set of topics organised by site, area and asset.",
    whyItMatters: "UNS breaks the point-to-point integration spaghetti that kills IIoT scalability.",
    relatedTerms: ["mqtt", "isa-95", "iiot"],
    searchKeywords: ["uns", "unified namespace"],
  }),
  e({
    slug: "isa-95",
    term: "ISA-95",
    category: "Industrial IoT & Automation",
    shortDefinition: "International standard defining integration between enterprise and control systems.",
    definition: "ISA-95 defines the terminology and hierarchical models (site, area, work centre, work unit) used to integrate ERP, MES/MOM and control systems. It is the reference model behind most modern MES architectures.",
    whyItMatters: "Following ISA-95 avoids inventing another proprietary integration model per project.",
    relatedTerms: ["mes", "mom", "unified-namespace"],
    searchKeywords: ["isa 95", "isa-95"],
  }),
  e({
    slug: "digital-twin",
    term: "Digital Twin",
    category: "Industrial IoT & Automation",
    shortDefinition: "Live digital representation of a physical asset or process.",
    definition: "A digital twin mirrors a physical asset, line or plant using real-time data. It ranges from simple asset dashboards to physics-based simulations used for what-if analysis and predictive maintenance.",
    whyItMatters: "Twins let engineering test changes and predict outcomes without stopping the real line.",
    relatedTerms: ["iiot", "predictive-analytics", "condition-monitoring"],
    searchKeywords: ["digital twin"],
  }),
  e({
    slug: "cloud-manufacturing",
    term: "Cloud Manufacturing",
    category: "Industrial IoT & Automation",
    shortDefinition: "Manufacturing applications delivered from cloud infrastructure.",
    definition: "Cloud Manufacturing refers to MES, analytics and IIoT platforms hosted on cloud infrastructure. It usually pairs with edge components on-prem to keep production running independently of cloud availability.",
    whyItMatters: "Cloud shortens deployment time and simplifies multi-site rollouts.",
    relatedTerms: ["edge-computing", "smart-manufacturing"],
    searchKeywords: ["cloud manufacturing"],
  }),
  e({
    slug: "predictive-analytics",
    term: "Predictive Analytics",
    category: "Industrial IoT & Automation",
    shortDefinition: "Statistical and ML techniques that forecast future events from historical data.",
    definition: "Predictive Analytics uses historical and streaming data to forecast future outcomes — machine failure, quality drift, energy spikes. In manufacturing, it depends on clean, contextualised MES and historian data.",
    whyItMatters: "Predictions are only useful when the underlying production data is trustworthy.",
    relatedTerms: ["predictive-maintenance", "anomaly-detection", "machine-learning-in-manufacturing"],
    searchKeywords: ["predictive analytics"],
  }),
  e({
    slug: "anomaly-detection",
    term: "Anomaly Detection",
    category: "Industrial IoT & Automation",
    shortDefinition: "Automated identification of unusual patterns in production data.",
    definition: "Anomaly Detection flags deviations from normal operating patterns — sudden vibration change, temperature spike, quality drift. It is often the first ML use case deployed on top of an MES/historian stack.",
    whyItMatters: "It surfaces early warnings that thresholds and rules alone would miss.",
    relatedTerms: ["predictive-analytics", "condition-monitoring"],
    searchKeywords: ["anomaly detection"],
  }),
  e({
    slug: "machine-learning-in-manufacturing",
    term: "Machine Learning in Manufacturing",
    category: "Industrial IoT & Automation",
    shortDefinition: "Applying ML models to production, quality and maintenance problems.",
    definition: "Machine Learning in manufacturing spans predictive maintenance, quality prediction, energy optimisation and process control. Its success depends on data volume, quality and clear ties to business KPIs.",
    whyItMatters: "ML fails without an MES-quality data foundation and a real operational question.",
    relatedTerms: ["predictive-analytics", "anomaly-detection"],
    searchKeywords: ["machine learning", "ml manufacturing"],
  }),
  e({
    slug: "industrial-network",
    term: "Industrial Network",
    aliases: ["Industrial Ethernet"],
    category: "Industrial IoT & Automation",
    shortDefinition: "Ruggedised networking used to connect industrial equipment.",
    definition: "An industrial network — often based on Industrial Ethernet variants such as Profinet or EtherNet/IP — provides deterministic, resilient communication between PLCs, drives, sensors and gateways.",
    whyItMatters: "IIoT programs succeed or fail on the state of the underlying industrial network.",
    relatedTerms: ["ot", "iiot-gateway", "modbus", "opc-ua"],
    searchKeywords: ["industrial network", "industrial ethernet"],
  }),
  e({
    slug: "remote-monitoring",
    term: "Remote Monitoring",
    category: "Industrial IoT & Automation",
    shortDefinition: "Monitoring production or equipment from off-site locations.",
    definition: "Remote Monitoring lets engineers, managers and OEMs view live production or asset data from outside the plant. It underpins multi-site oversight and remote service contracts.",
    whyItMatters: "It removes distance as a constraint on expert intervention.",
    relatedTerms: ["real-time-monitoring", "cloud-manufacturing"],
    searchKeywords: ["remote monitoring"],
  }),

  // ============ Energy Management ============
  e({
    slug: "iso-50001",
    term: "ISO 50001",
    category: "Energy Management",
    shortDefinition: "International standard for Energy Management Systems.",
    definition: "ISO 50001 defines requirements for establishing, implementing and improving an Energy Management System. It formalises baselines, EnPIs, Significant Energy Uses and continuous improvement of energy performance.",
    whyItMatters: "Certification against ISO 50001 is increasingly a requirement in EU markets and industrial customers.",
    relatedTerms: ["enms", "enpi", "energy-baseline", "sec"],
    searchKeywords: ["iso 50001"],
  }),
  e({
    slug: "enpi",
    term: "EnPI",
    fullName: "Energy Performance Indicator",
    acronym: "EnPI",
    aliases: ["Energy Performance Indicator"],
    category: "Energy Management",
    shortDefinition: "Metric that quantifies energy performance relative to a baseline.",
    definition: "An EnPI expresses energy performance in a form comparable over time — for example kWh per ton produced. Under ISO 50001, EnPIs are set against a baseline and monitored to demonstrate improvement.",
    whyItMatters: "EnPIs make energy performance auditable and comparable across products and periods.",
    relatedTerms: ["enms", "energy-baseline", "sec", "iso-50001"],
    searchKeywords: ["enpi"],
  }),
  e({
    slug: "energy-baseline",
    term: "Energy Baseline",
    fullName: "Energy Baseline",
    acronym: "EnB",
    aliases: ["EnB"],
    category: "Energy Management",
    shortDefinition: "Reference period against which future energy performance is compared.",
    definition: "An Energy Baseline (EnB) is a documented period of energy performance used as the reference for EnPIs. Adjustments are made for production volume, product mix and weather to keep comparisons fair.",
    whyItMatters: "Without a defined baseline, energy savings claims cannot be verified.",
    relatedTerms: ["enpi", "enms", "iso-50001"],
    searchKeywords: ["energy baseline", "enb"],
  }),
  e({
    slug: "sec",
    term: "SEC",
    fullName: "Specific Energy Consumption",
    acronym: "SEC",
    aliases: ["Specific Energy Consumption"],
    category: "Energy Management",
    shortDefinition: "Energy used per unit of output.",
    definition: "SEC expresses energy consumption relative to production — kWh per ton, m³ of gas per unit, etc. It is the most common EnPI on the shop floor and links energy directly to production performance.",
    formula: "SEC = Energy Consumed / Units Produced",
    whyItMatters: "SEC per SKU exposes energy-hungry products and machines that a total bill hides.",
    relatedTerms: ["enpi", "energy-intensity", "energy-per-unit-produced"],
    searchKeywords: ["sec", "specific energy consumption"],
  }),
  e({
    slug: "energy-intensity",
    term: "Energy Intensity",
    category: "Energy Management",
    shortDefinition: "Energy consumed per unit of economic output.",
    definition: "Energy Intensity is a higher-level EnPI — energy per revenue, per value added or per m². It is used by leadership and reporting frameworks such as CSRD.",
    whyItMatters: "Energy Intensity is what investors and regulators increasingly ask about.",
    relatedTerms: ["sec", "carbon-intensity", "enpi"],
    searchKeywords: ["energy intensity"],
  }),
  e({
    slug: "energy-efficiency",
    term: "Energy Efficiency",
    category: "Energy Management",
    shortDefinition: "Ratio of useful output to energy input.",
    definition: "Energy Efficiency measures how much of the energy consumed is converted into useful output rather than losses. Improvements come from equipment upgrades, control strategies and better process operation.",
    whyItMatters: "Efficiency projects compete for capex with production projects; live EnPIs make the business case.",
    relatedTerms: ["enms", "sec", "energy-loss"],
    searchKeywords: ["energy efficiency"],
  }),
  e({
    slug: "energy-monitoring",
    term: "Energy Monitoring",
    category: "Energy Management",
    shortDefinition: "Continuous measurement of energy consumption per asset or process.",
    definition: "Energy Monitoring collects electricity, gas, steam, water and compressed-air data at meter or sub-meter level and links it to production. It is the practical foundation of any EnMS.",
    whyItMatters: "Monthly bills are too late; monitoring makes energy an operational, not financial, KPI.",
    relatedTerms: ["energy-metering", "submetering", "smart-meter"],
    searchKeywords: ["energy monitoring"],
  }),
  e({
    slug: "energy-metering",
    term: "Energy Metering",
    category: "Energy Management",
    shortDefinition: "Measuring energy flows with dedicated meters.",
    definition: "Energy Metering uses electricity, gas, steam or water meters to quantify consumption. Modern meters provide digital outputs (Modbus, pulse) that feed directly into EnMS platforms.",
    whyItMatters: "You cannot manage what you do not meter — starting with the biggest loads.",
    relatedTerms: ["submetering", "smart-meter", "energy-monitoring"],
    searchKeywords: ["energy metering"],
  }),
  e({
    slug: "submetering",
    term: "Submetering",
    category: "Energy Management",
    shortDefinition: "Adding meters below the utility meter to see consumption by area or asset.",
    definition: "Submetering breaks total plant consumption into meaningful buckets — line, area, compressor, chiller — so responsibility and savings opportunities become clear.",
    whyItMatters: "Submetering turns a shared bill into per-line P&L visibility.",
    relatedTerms: ["energy-metering", "smart-meter"],
    searchKeywords: ["submetering"],
  }),
  e({
    slug: "smart-meter",
    term: "Smart Meter",
    category: "Energy Management",
    shortDefinition: "Electronic meter with digital communication and higher-frequency readings.",
    definition: "Smart meters provide interval readings (typically 1–15 minutes) and expose data via digital protocols. They enable load-profile analysis, peak monitoring and integration with EnMS.",
    whyItMatters: "Smart meters are what make sub-hourly demand analysis practical.",
    relatedTerms: ["energy-metering", "submetering", "peak-demand"],
    searchKeywords: ["smart meter"],
  }),
  e({
    slug: "electricity-consumption",
    term: "Electricity Consumption",
    category: "Energy Management",
    shortDefinition: "Total electrical energy used over a period, measured in kWh.",
    definition: "Electricity Consumption is cumulative energy drawn from the grid or on-site generation, typically expressed in kWh. It is the primary input for cost, EnPI and CO₂ calculations in most factories.",
    whyItMatters: "Consumption tracked per shift and per product reveals losses invisible in monthly bills.",
    relatedTerms: ["kwh", "energy-monitoring", "sec"],
    searchKeywords: ["electricity consumption"],
  }),
  e({
    slug: "kwh",
    term: "kWh",
    category: "Energy Management",
    shortDefinition: "Kilowatt-hour — standard unit of electrical energy.",
    definition: "The kilowatt-hour (kWh) is the standard unit of electrical energy: one kilowatt of power for one hour. Almost all industrial energy KPIs (SEC, EnPI, cost) are expressed per kWh.",
    whyItMatters: "kWh is the shared unit that lets operations, finance and sustainability speak the same language.",
    relatedTerms: ["electricity-consumption", "peak-demand", "power-factor"],
    searchKeywords: ["kwh", "kilowatt hour"],
  }),
  e({
    slug: "peak-demand",
    term: "Peak Demand",
    aliases: ["Maximum Demand"],
    category: "Energy Management",
    shortDefinition: "Highest power draw during a billing interval.",
    definition: "Peak Demand (or Maximum Demand) is the highest instantaneous or interval power draw, typically in kW or kVA. Utilities often charge a substantial fee based on it, in addition to energy usage.",
    whyItMatters: "Managing peaks can cut energy cost significantly without reducing total consumption.",
    relatedTerms: ["demand-response", "load-profile", "smart-meter"],
    searchKeywords: ["peak demand", "maximum demand"],
  }),
  e({
    slug: "demand-response",
    term: "Demand Response",
    category: "Energy Management",
    shortDefinition: "Shifting or reducing electrical load in response to grid or price signals.",
    definition: "Demand Response programs pay industrial consumers to reduce or reschedule load during peak grid events. Participating requires accurate real-time metering and the ability to control specific loads.",
    whyItMatters: "Demand Response turns flexibility into revenue and improves grid resilience.",
    relatedTerms: ["peak-demand", "load-profile", "energy-monitoring"],
    searchKeywords: ["demand response"],
  }),
  e({
    slug: "load-profile",
    term: "Load Profile",
    category: "Energy Management",
    shortDefinition: "Pattern of electrical demand over time.",
    definition: "A Load Profile plots power draw across hours, days or weeks. Analysing it exposes idle consumption, avoidable peaks and process patterns that can be optimised.",
    whyItMatters: "Load profiles are usually the first artefact that unlocks energy savings ideas.",
    relatedTerms: ["peak-demand", "base-load", "smart-meter"],
    searchKeywords: ["load profile"],
  }),
  e({
    slug: "base-load",
    term: "Base Load",
    category: "Energy Management",
    shortDefinition: "Constant background energy consumption regardless of production.",
    definition: "Base Load is the energy consumed even when no production is running — lighting, HVAC, standby equipment, compressed-air leaks. Reducing base load often delivers cheap, fast savings.",
    whyItMatters: "Base load is a direct measurement of waste; every kW cut goes straight to margin.",
    relatedTerms: ["load-profile", "energy-loss", "energy-efficiency"],
    searchKeywords: ["base load"],
  }),
  e({
    slug: "power-factor",
    term: "Power Factor",
    category: "Energy Management",
    shortDefinition: "Ratio of real power to apparent power in an AC system.",
    definition: "Power Factor (0 to 1) measures how effectively current is being converted into useful work. Low power factor increases losses and often triggers utility penalties; correction uses capacitor banks or active filters.",
    whyItMatters: "Correcting power factor removes penalty charges and reduces system losses.",
    relatedTerms: ["reactive-power", "electricity-consumption"],
    searchKeywords: ["power factor"],
  }),
  e({
    slug: "reactive-power",
    term: "Reactive Power",
    category: "Energy Management",
    shortDefinition: "Non-productive power that oscillates between source and load.",
    definition: "Reactive Power (measured in kVAr) does no useful work but is required by inductive loads such as motors and transformers. Excess reactive power lowers power factor and can incur utility penalties.",
    whyItMatters: "Managing reactive power protects transformers and reduces electricity bills.",
    relatedTerms: ["power-factor", "electricity-consumption"],
    searchKeywords: ["reactive power"],
  }),
  e({
    slug: "energy-cost-allocation",
    term: "Energy Cost Allocation",
    category: "Energy Management",
    shortDefinition: "Assigning energy cost to products, orders or cost centres.",
    definition: "Energy Cost Allocation distributes the energy bill across products, orders, work centres or business units using metering and production data. It supports accurate product costing and departmental accountability.",
    whyItMatters: "Allocation makes energy visible in P&L discussions, not just utility bills.",
    relatedTerms: ["submetering", "sec", "energy-per-unit-produced"],
    searchKeywords: ["energy cost allocation"],
  }),
  e({
    slug: "energy-loss",
    term: "Energy Loss",
    category: "Energy Management",
    shortDefinition: "Energy consumed that does not contribute to useful output.",
    definition: "Energy Loss covers idling equipment, compressed-air leaks, poor insulation, low power factor and process inefficiency. It is quantified by comparing actual consumption to a theoretical minimum for the same output.",
    whyItMatters: "Naming and measuring losses is the first step in an energy improvement program.",
    relatedTerms: ["base-load", "energy-efficiency", "energy-audit"],
    searchKeywords: ["energy loss"],
  }),
  e({
    slug: "energy-audit",
    term: "Energy Audit",
    category: "Energy Management",
    shortDefinition: "Structured assessment of energy use and improvement opportunities.",
    definition: "An Energy Audit systematically maps energy flows, identifies Significant Energy Uses and quantifies savings opportunities. It is often the input to ISO 50001 certification and capital investment cases.",
    whyItMatters: "Audits turn 'we use too much energy' into a prioritised, budgeted project list.",
    relatedTerms: ["enms", "iso-50001", "energy-loss"],
    searchKeywords: ["energy audit"],
  }),
  e({
    slug: "carbon-intensity",
    term: "Carbon Intensity",
    category: "Energy Management",
    shortDefinition: "CO₂-equivalent emissions per unit of output.",
    definition: "Carbon Intensity expresses emissions per unit of product, revenue or activity. It is derived from energy consumption combined with grid and fuel emission factors.",
    whyItMatters: "Customers and regulators increasingly demand product-level carbon numbers.",
    relatedTerms: ["scope-1-emissions", "scope-2-emissions", "energy-intensity"],
    searchKeywords: ["carbon intensity", "co2"],
  }),
  e({
    slug: "scope-1-emissions",
    term: "Scope 1 Emissions",
    category: "Energy Management",
    shortDefinition: "Direct emissions from sources the company owns or controls.",
    definition: "Scope 1 covers direct greenhouse-gas emissions from on-site combustion, process emissions and company vehicles. It is calculated from measured fuel and process data.",
    whyItMatters: "Scope 1 is the emissions category the plant can most directly reduce.",
    relatedTerms: ["scope-2-emissions", "carbon-intensity"],
    searchKeywords: ["scope 1"],
  }),
  e({
    slug: "scope-2-emissions",
    term: "Scope 2 Emissions",
    category: "Energy Management",
    shortDefinition: "Indirect emissions from purchased electricity, heat or steam.",
    definition: "Scope 2 covers emissions embedded in purchased energy. Reductions come from lower consumption, cleaner grid mix, PPAs or on-site renewables.",
    whyItMatters: "Scope 2 reporting is required by most sustainability frameworks and is directly linked to energy monitoring.",
    relatedTerms: ["scope-1-emissions", "electricity-consumption", "carbon-intensity"],
    searchKeywords: ["scope 2"],
  }),
  e({
    slug: "production-energy-consumption",
    term: "Production Energy Consumption",
    aliases: ["Energy per Unit Produced"],
    category: "Energy Management",
    shortDefinition: "Energy used specifically to produce output, excluding idle and support loads.",
    definition: "Production Energy Consumption isolates the energy attributable to actual production, enabling a true 'energy per unit produced' KPI. It requires correlating meter data with MES order and cycle data.",
    whyItMatters: "This is the KPI operators can influence shift by shift.",
    relatedTerms: ["sec", "energy-cost-allocation", "enpi"],
    searchKeywords: ["energy per unit", "production energy"],
  }),

  // ============ Quality Management ============
  e({
    slug: "iso-9001",
    term: "ISO 9001",
    category: "Quality Management",
    shortDefinition: "International standard for quality management systems.",
    definition: "ISO 9001 specifies requirements for a QMS focused on customer satisfaction, process approach and continual improvement. It is the most widely used quality standard across manufacturing.",
    whyItMatters: "Certification is a baseline requirement for many industrial and OEM customers.",
    relatedTerms: ["qms", "capa", "spc"],
    searchKeywords: ["iso 9001"],
  }),
  e({
    slug: "quality-control",
    term: "Quality Control",
    category: "Quality Management",
    shortDefinition: "Operational activities that verify product meets specification.",
    definition: "Quality Control (QC) is the set of inspections, tests and measurements performed on products and processes to confirm conformance. Digital QC replaces paper checklists with tablet-based inspections linked to batch data.",
    whyItMatters: "Digital QC eliminates transcription errors and creates a searchable quality record.",
    relatedTerms: ["quality-assurance", "inspection-plan", "spc"],
    searchKeywords: ["quality control", "qc"],
  }),
  e({
    slug: "quality-assurance",
    term: "Quality Assurance",
    category: "Quality Management",
    shortDefinition: "System-level activities designed to prevent quality problems.",
    definition: "Quality Assurance (QA) covers process design, procedures, audits and training that prevent defects from happening. Where QC catches problems, QA aims to make them impossible.",
    whyItMatters: "Strong QA reduces the cost of QC by shifting effort from detection to prevention.",
    relatedTerms: ["quality-control", "iso-9001", "poka-yoke"],
    searchKeywords: ["quality assurance", "qa"],
  }),
  e({
    slug: "spc",
    term: "SPC",
    fullName: "Statistical Process Control",
    acronym: "SPC",
    aliases: ["Statistical Process Control"],
    category: "Quality Management",
    shortDefinition: "Statistical methods to monitor and control process variation.",
    definition: "SPC uses control charts and capability indices (Cp, Cpk) to detect process drift before it produces defects. Combined with digital quality checks and real-time machine data, SPC becomes an early-warning system rather than a post-mortem tool.",
    whyItMatters: "SPC prevents scrap by catching drift while the process is still in control.",
    relatedTerms: ["control-chart", "process-capability", "cp", "cpk"],
    searchKeywords: ["spc"],
  }),
  e({
    slug: "control-chart",
    term: "Control Chart",
    category: "Quality Management",
    shortDefinition: "SPC chart that plots a process variable against control limits over time.",
    definition: "A Control Chart tracks a measured variable against statistically derived upper and lower control limits. Points outside limits or non-random patterns signal process changes that require action.",
    whyItMatters: "Control charts distinguish 'noise' from real change so teams do not overreact.",
    relatedTerms: ["spc", "process-capability"],
    searchKeywords: ["control chart"],
  }),
  e({
    slug: "process-capability",
    term: "Process Capability",
    category: "Quality Management",
    shortDefinition: "Ability of a process to consistently produce within specification.",
    definition: "Process Capability quantifies how well a stable process fits within specification limits, expressed as Cp and Cpk. High capability means fewer defects and less inspection required.",
    whyItMatters: "Capability drives whether inspection can be reduced without risking quality.",
    relatedTerms: ["cp", "cpk", "spc"],
    searchKeywords: ["process capability"],
  }),
  e({
    slug: "cp",
    term: "Cp",
    category: "Quality Management",
    shortDefinition: "Process capability index comparing spec width to process spread.",
    definition: "Cp compares the tolerance width to the process spread (6σ). A Cp of 1.33 or higher is a common minimum target. Cp assumes the process is centred on the target.",
    formula: "Cp = (USL − LSL) / (6σ)",
    whyItMatters: "Cp answers 'is the process tight enough?' independent of whether it is centred.",
    relatedTerms: ["cpk", "process-capability", "spc"],
    searchKeywords: ["cp", "capability index"],
  }),
  e({
    slug: "cpk",
    term: "Cpk",
    category: "Quality Management",
    shortDefinition: "Process capability index that also accounts for process centring.",
    definition: "Cpk measures capability relative to the nearest specification limit, so it penalises off-centre processes. A Cpk of 1.33 or higher is a common minimum; automotive customers often require 1.67 or more.",
    formula: "Cpk = min[(USL − μ)/(3σ), (μ − LSL)/(3σ)]",
    whyItMatters: "Cpk is what most customer specs actually require, not Cp.",
    relatedTerms: ["cp", "process-capability", "spc"],
    searchKeywords: ["cpk"],
  }),
  e({
    slug: "non-conformance",
    term: "Non-Conformance",
    fullName: "Non-Conformance Report",
    acronym: "NCR",
    aliases: ["NCR"],
    category: "Quality Management",
    shortDefinition: "Documented event of a product or process not meeting requirements.",
    definition: "A Non-Conformance Report (NCR) records what failed, where, when and by whom. NCRs trigger containment, investigation and CAPA. Digital NCRs linked to batch and machine data enable real root-cause analysis.",
    whyItMatters: "NCRs are the raw material of continuous improvement — if actually analysed.",
    relatedTerms: ["capa", "root-cause-analysis", "8d"],
    searchKeywords: ["ncr", "non conformance"],
  }),
  e({
    slug: "capa",
    term: "CAPA",
    fullName: "Corrective and Preventive Action",
    acronym: "CAPA",
    aliases: ["Corrective and Preventive Action"],
    category: "Quality Management",
    shortDefinition: "Process for correcting existing problems and preventing recurrence.",
    definition: "CAPA is a structured workflow that turns non-conformances and audit findings into corrective actions (fix the current problem) and preventive actions (stop recurrence). It is a required element of ISO 9001 and many regulated industries.",
    whyItMatters: "CAPA converts one-off incidents into permanent process improvements.",
    relatedTerms: ["non-conformance", "root-cause-analysis", "8d"],
    searchKeywords: ["capa"],
  }),
  e({
    slug: "fmea",
    term: "FMEA",
    fullName: "Failure Mode and Effects Analysis",
    acronym: "FMEA",
    aliases: ["Failure Mode and Effects Analysis"],
    category: "Quality Management",
    shortDefinition: "Structured method to identify and prioritise potential failure modes.",
    definition: "FMEA ranks potential failure modes by severity, occurrence and detection to prioritise mitigations. It is standard practice in automotive, aerospace and medical device manufacturing.",
    whyItMatters: "FMEA forces engineering to design out risk instead of relying on inspection.",
    relatedTerms: ["root-cause-analysis", "capa", "poka-yoke"],
    searchKeywords: ["fmea"],
  }),
  e({
    slug: "root-cause-analysis",
    term: "Root Cause Analysis",
    category: "Quality Management",
    shortDefinition: "Systematic investigation of the underlying cause of a problem.",
    definition: "Root Cause Analysis (RCA) uses methods like 5 Whys, fishbone diagrams and 8D to identify the true cause of a defect or failure, not just symptoms. Real MES and quality data make RCA far faster and more accurate.",
    whyItMatters: "Without RCA, corrective actions treat symptoms and problems return.",
    relatedTerms: ["5-whys", "8d", "capa", "fmea"],
    searchKeywords: ["rca", "root cause"],
  }),
  e({
    slug: "5-whys",
    term: "5 Whys",
    category: "Quality Management",
    shortDefinition: "Root-cause technique that asks 'why?' repeatedly to reach the underlying cause.",
    definition: "5 Whys is a simple RCA technique: keep asking 'why did that happen?' — usually about five times — until you reach a cause that, if removed, would prevent recurrence.",
    whyItMatters: "5 Whys is the cheapest, fastest RCA tool and works in any team meeting.",
    relatedTerms: ["root-cause-analysis", "kaizen", "8d"],
    searchKeywords: ["5 whys", "five whys"],
  }),
  e({
    slug: "8d",
    term: "8D Problem Solving",
    aliases: ["8D"],
    category: "Quality Management",
    shortDefinition: "Eight-discipline structured problem-solving methodology.",
    definition: "8D is an 8-step framework — team, describe, contain, root cause, corrective action, implement, prevent, close — widely used in automotive customer complaint handling.",
    whyItMatters: "8D is often contractually required by OEM customers when defects escape.",
    relatedTerms: ["capa", "root-cause-analysis", "fmea"],
    searchKeywords: ["8d"],
  }),
  e({
    slug: "pareto-analysis",
    term: "Pareto Analysis",
    category: "Quality Management",
    shortDefinition: "Ranking causes by impact to focus on the vital few.",
    definition: "Pareto Analysis charts causes of losses or defects in descending order to identify the roughly 20% of causes that generate 80% of the problem. Automatic reason-coded downtime and scrap capture make Pareto reports trustworthy.",
    whyItMatters: "Pareto discipline stops teams from spreading effort over trivia.",
    relatedTerms: ["kaizen", "six-big-losses", "root-cause-analysis"],
    searchKeywords: ["pareto"],
  }),
  e({
    slug: "poka-yoke",
    term: "Poka-Yoke",
    category: "Quality Management",
    shortDefinition: "Mistake-proofing device or design that prevents human error.",
    definition: "Poka-Yoke means designing processes and tooling so that mistakes are physically impossible or immediately detectable — asymmetrical connectors, sensor interlocks, forced sequences.",
    whyItMatters: "Poka-Yoke removes reliance on operator vigilance, which always eventually fails.",
    relatedTerms: ["fmea", "jidoka", "quality-assurance"],
    searchKeywords: ["poka yoke", "mistake proofing"],
  }),
  e({
    slug: "inspection-plan",
    term: "Inspection Plan",
    category: "Quality Management",
    shortDefinition: "Defined checks, sampling and criteria for a product or process.",
    definition: "An Inspection Plan specifies which characteristics are inspected, at which stage, by whom, with what tools and against what limits. In a digital QMS it becomes the dynamic checklist operators execute on tablets.",
    whyItMatters: "A shared, versioned inspection plan eliminates 'we didn't know we had to check that'.",
    relatedTerms: ["quality-control", "in-process-inspection", "final-inspection"],
    searchKeywords: ["inspection plan", "qcp"],
  }),
  e({
    slug: "quality-inspection",
    term: "Quality Inspection",
    category: "Quality Management",
    shortDefinition: "Act of examining a product or process against specification.",
    definition: "Quality Inspection is the physical or digital examination of products and processes at defined points. It may be manual, sensor-based or automated using vision systems.",
    whyItMatters: "Well-designed inspection catches defects at the earliest, cheapest point.",
    relatedTerms: ["in-process-inspection", "final-inspection", "inspection-plan"],
    searchKeywords: ["quality inspection"],
  }),
  e({
    slug: "in-process-inspection",
    term: "In-Process Inspection",
    category: "Quality Management",
    shortDefinition: "Inspection performed while product is still being produced.",
    definition: "In-Process Inspection catches issues at intermediate stages — often via SPC sampling or 100% automated measurement — before more value is added to a defective unit.",
    whyItMatters: "Catching defects mid-process saves the labour, energy and material of downstream operations.",
    relatedTerms: ["spc", "final-inspection", "quality-inspection"],
    searchKeywords: ["in process inspection"],
  }),
  e({
    slug: "final-inspection",
    term: "Final Inspection",
    category: "Quality Management",
    shortDefinition: "Last quality check before shipping.",
    definition: "Final Inspection verifies finished products meet all requirements before dispatch. It is the last gate against defects reaching the customer and often produces the Certificate of Analysis.",
    whyItMatters: "Robust final inspection protects the customer; smart upstream inspection protects the plant.",
    relatedTerms: ["coa", "quality-inspection", "in-process-inspection"],
    searchKeywords: ["final inspection"],
  }),
  e({
    slug: "defect",
    term: "Defect",
    category: "Quality Management",
    shortDefinition: "Any non-conformance of a product with specification.",
    definition: "A defect is any deviation from specification that renders a unit unacceptable without rework. Defects are classified by type and severity and used to compute Defect Rate and yield.",
    whyItMatters: "Consistent defect classification is what makes cross-shift trend analysis meaningful.",
    relatedTerms: ["defect-rate", "scrap", "rework"],
    searchKeywords: ["defect"],
  }),
  e({
    slug: "defect-rate",
    term: "Defect Rate",
    category: "Quality Management",
    shortDefinition: "Number of defective units per units produced.",
    definition: "Defect Rate expresses defects per unit, per thousand or per million produced (ppm). It is the operational counterpart to yield and a common customer-facing metric.",
    formula: "Defect Rate = Defects / Units Produced",
    whyItMatters: "Defect Rate is what customer scorecards usually measure.",
    relatedTerms: ["reject-rate", "yield", "first-pass-yield"],
    searchKeywords: ["defect rate", "ppm"],
  }),
  e({
    slug: "coa",
    term: "COA",
    fullName: "Certificate of Analysis",
    acronym: "COA",
    aliases: ["Certificate of Analysis"],
    category: "Quality Management",
    shortDefinition: "Document confirming a batch meets defined quality parameters.",
    definition: "A Certificate of Analysis (COA) lists the measured properties of a shipped batch — chemical composition, mechanical properties, tests performed. It is a common regulatory and customer requirement in metals, chemicals, food and pharma.",
    whyItMatters: "Automated COA generation from LIMS/MES cuts hours of manual admin per shipment.",
    relatedTerms: ["lims", "quality-inspection", "traceability"],
    searchKeywords: ["coa", "certificate of analysis"],
  }),
  e({
    slug: "product-traceability",
    term: "Product Traceability",
    category: "Quality Management",
    shortDefinition: "Ability to reconstruct the production history of a specific unit.",
    definition: "Product Traceability records which materials, machine, operator and settings produced a given unit or batch, enabling targeted recalls and warranty investigations.",
    whyItMatters: "Regulated industries and OEM customers make traceability a market-access requirement.",
    relatedTerms: ["material-traceability", "genealogy", "serialization"],
    searchKeywords: ["product traceability"],
  }),
  e({
    slug: "material-traceability",
    term: "Material Traceability",
    category: "Quality Management",
    shortDefinition: "Ability to trace raw materials through production and into finished goods.",
    definition: "Material Traceability records which raw-material lots were consumed by each production order, so a supplier issue can be linked to specific finished goods.",
    whyItMatters: "It turns supplier recalls from a plant-wide crisis into a targeted response.",
    relatedTerms: ["genealogy", "product-traceability", "batch"],
    searchKeywords: ["material traceability"],
  }),
  e({
    slug: "genealogy",
    term: "Genealogy",
    aliases: ["Product Genealogy"],
    category: "Quality Management",
    shortDefinition: "Full parent-child relationship of materials, batches and units produced.",
    definition: "Product Genealogy links every finished unit or batch to its inputs (materials, sub-assemblies), producing equipment, operators and quality events. It is the technical foundation of true traceability.",
    whyItMatters: "Genealogy is what turns 'traceable' from a claim into a queryable fact.",
    relatedTerms: ["product-traceability", "material-traceability", "batch-tracking"],
    searchKeywords: ["genealogy"],
  }),
  e({
    slug: "serialization",
    term: "Serialization",
    category: "Quality Management",
    shortDefinition: "Assigning a unique identifier to each produced unit.",
    definition: "Serialization gives every unit a globally unique ID, usually printed as a barcode, QR or data-matrix. It underpins unit-level traceability, warranty and anti-counterfeiting programs.",
    whyItMatters: "Serialization is often a regulatory requirement (pharma, tobacco, EV batteries).",
    relatedTerms: ["serial-number", "product-traceability", "genealogy"],
    searchKeywords: ["serialization"],
  }),
  e({
    slug: "batch-quality",
    term: "Batch Quality",
    aliases: ["Lot Quality"],
    category: "Quality Management",
    shortDefinition: "Aggregate quality results for a specific batch or lot.",
    definition: "Batch Quality summarises inspection results, deviations and holds for a production batch or lot, driving release-to-ship decisions and COA generation.",
    whyItMatters: "Batch-level quality decisions require batch-level data — not shift or daily aggregates.",
    relatedTerms: ["batch", "lot", "coa"],
    searchKeywords: ["batch quality", "lot quality"],
  }),

  // ============ Maintenance & Reliability ============
  e({
    slug: "preventive-maintenance",
    term: "Preventive Maintenance",
    category: "Maintenance & Reliability",
    shortDefinition: "Scheduled maintenance performed to prevent failure.",
    definition: "Preventive Maintenance (PM) is time- or usage-based maintenance performed regardless of equipment condition. It reduces unplanned failures but can also over-maintain healthy assets.",
    whyItMatters: "PM is the baseline every reliability program improves upon with CBM and PdM.",
    relatedTerms: ["predictive-maintenance", "cbm", "cmms"],
    searchKeywords: ["preventive maintenance", "pm"],
  }),
  e({
    slug: "predictive-maintenance",
    term: "Predictive Maintenance",
    category: "Maintenance & Reliability",
    shortDefinition: "Maintenance triggered by predictive analysis of equipment data.",
    definition: "Predictive Maintenance (PdM) uses condition monitoring and analytics to predict when a failure is likely, so intervention happens just in time. It requires reliable sensor data and analytics on top of CMMS.",
    whyItMatters: "PdM lowers both unplanned failures and unnecessary PM.",
    relatedTerms: ["condition-monitoring", "cbm", "anomaly-detection"],
    searchKeywords: ["predictive maintenance", "pdm"],
  }),
  e({
    slug: "cbm",
    term: "Condition-Based Maintenance",
    aliases: ["CBM"],
    category: "Maintenance & Reliability",
    shortDefinition: "Maintenance performed when condition indicators exceed thresholds.",
    definition: "CBM triggers work based on measured equipment condition — vibration, temperature, current — rather than time. It sits between preventive and fully predictive maintenance.",
    whyItMatters: "CBM is often the pragmatic first step beyond calendar PM.",
    relatedTerms: ["condition-monitoring", "predictive-maintenance", "rcm"],
    searchKeywords: ["cbm", "condition based maintenance"],
  }),
  e({
    slug: "corrective-maintenance",
    term: "Corrective Maintenance",
    category: "Maintenance & Reliability",
    shortDefinition: "Maintenance performed to restore equipment after a fault is detected.",
    definition: "Corrective Maintenance repairs equipment after a fault or performance deviation. It can be planned (deferred to a maintenance window) or unplanned (immediate).",
    whyItMatters: "Tracking corrective work separately reveals the true cost of reactive strategies.",
    relatedTerms: ["reactive-maintenance", "planned-maintenance", "breakdown"],
    searchKeywords: ["corrective maintenance"],
  }),
  e({
    slug: "reactive-maintenance",
    term: "Reactive Maintenance",
    aliases: ["Breakdown Maintenance"],
    category: "Maintenance & Reliability",
    shortDefinition: "Unplanned maintenance in response to equipment failure.",
    definition: "Reactive Maintenance (also Breakdown Maintenance) is repair after failure. It is expensive due to unplanned downtime, secondary damage and premium labour, but sometimes acceptable for non-critical assets.",
    whyItMatters: "Excessive reactive work is a leading indicator of reliability program failure.",
    relatedTerms: ["corrective-maintenance", "breakdown", "mtbf"],
    searchKeywords: ["reactive maintenance", "breakdown maintenance"],
  }),
  e({
    slug: "planned-maintenance",
    term: "Planned Maintenance",
    category: "Maintenance & Reliability",
    shortDefinition: "Maintenance work scheduled in advance with resources and materials arranged.",
    definition: "Planned Maintenance is any maintenance (preventive, corrective or predictive) with resources, parts and time booked in advance. It is far cheaper and faster than reactive work.",
    whyItMatters: "Increasing the share of planned work is a core CMMS/EAM KPI.",
    relatedTerms: ["preventive-maintenance", "maintenance-schedule", "cmms"],
    searchKeywords: ["planned maintenance"],
  }),
  e({
    slug: "maintenance-work-order",
    term: "Maintenance Work Order",
    category: "Maintenance & Reliability",
    shortDefinition: "Documented instruction to perform a specific maintenance task.",
    definition: "A Maintenance Work Order defines what work is required, on which asset, by whom, using which materials, and captures the actual time, parts and findings after completion. It is the atomic unit of CMMS execution.",
    whyItMatters: "Work-order data is the foundation of reliability analytics and cost tracking.",
    relatedTerms: ["cmms", "planned-maintenance", "maintenance-schedule"],
    searchKeywords: ["work order"],
  }),
  e({
    slug: "maintenance-schedule",
    term: "Maintenance Schedule",
    category: "Maintenance & Reliability",
    shortDefinition: "Time-phased plan of maintenance activities across assets.",
    definition: "The Maintenance Schedule sequences preventive, predictive and planned corrective work across assets, aligned with production plans to minimise disruption.",
    whyItMatters: "A production-aware schedule prevents PMs from becoming their own source of downtime.",
    relatedTerms: ["planned-maintenance", "cmms", "production-scheduling"],
    searchKeywords: ["maintenance schedule"],
  }),
  e({
    slug: "asset-reliability",
    term: "Asset Reliability",
    aliases: ["Equipment Reliability"],
    category: "Maintenance & Reliability",
    shortDefinition: "Probability that an asset performs its function over a period.",
    definition: "Asset Reliability is the statistical likelihood that equipment performs as required for a defined period, expressed through MTBF, failure rate and availability.",
    whyItMatters: "Reliability is what actually determines production capacity in a mature plant.",
    relatedTerms: ["mtbf", "mttr", "failure-rate"],
    searchKeywords: ["reliability", "equipment reliability"],
  }),
  e({
    slug: "mtbf",
    term: "MTBF",
    fullName: "Mean Time Between Failures",
    acronym: "MTBF",
    aliases: ["Mean Time Between Failures"],
    category: "Maintenance & Reliability",
    shortDefinition: "Average operating time between failures of a repairable asset.",
    definition: "MTBF is total operating time divided by number of failures during that period. Higher MTBF means fewer breakdowns. It is a core reliability KPI, especially useful when compared over time or between similar assets.",
    formula: "MTBF = Total Operating Time / Number of Failures",
    whyItMatters: "MTBF trend, not absolute value, tells you whether reliability is improving.",
    relatedTerms: ["mttr", "mttf", "failure-rate"],
    searchKeywords: ["mtbf"],
  }),
  e({
    slug: "mttr",
    term: "MTTR",
    fullName: "Mean Time to Repair",
    acronym: "MTTR",
    aliases: ["Mean Time to Repair"],
    category: "Maintenance & Reliability",
    shortDefinition: "Average time to restore an asset after failure.",
    definition: "MTTR is total repair time divided by number of repairs, including diagnosis, parts wait and actual fix. It captures how efficiently the maintenance organisation responds when things break.",
    formula: "MTTR = Total Repair Time / Number of Repairs",
    whyItMatters: "Cutting MTTR often unlocks more availability than cutting failures.",
    relatedTerms: ["mtbf", "downtime", "cmms"],
    searchKeywords: ["mttr"],
  }),
  e({
    slug: "mttf",
    term: "MTTF",
    fullName: "Mean Time to Failure",
    acronym: "MTTF",
    aliases: ["Mean Time to Failure"],
    category: "Maintenance & Reliability",
    shortDefinition: "Average operating time until failure of a non-repairable asset.",
    definition: "MTTF applies to non-repairable items (bearings, lamps, sensors) — expected operating time until failure. It is used for spare-parts planning and warranty calculations.",
    whyItMatters: "MTTF drives spare-part stocking and replacement policies.",
    relatedTerms: ["mtbf", "failure-rate", "spare-parts-management"],
    searchKeywords: ["mttf"],
  }),
  e({
    slug: "failure-rate",
    term: "Failure Rate",
    category: "Maintenance & Reliability",
    shortDefinition: "Frequency of failures over a defined period.",
    definition: "Failure Rate expresses how often an asset fails, typically as failures per operating hour. It follows characteristic patterns (bathtub curve) that inform maintenance strategy.",
    whyItMatters: "Failure-rate trends reveal wear-out phases and PM plan adequacy.",
    relatedTerms: ["mtbf", "mttf", "asset-reliability"],
    searchKeywords: ["failure rate"],
  }),
  e({
    slug: "breakdown",
    term: "Breakdown",
    aliases: ["Machine Failure"],
    category: "Maintenance & Reliability",
    shortDefinition: "Unplanned equipment failure that stops production.",
    definition: "A breakdown is an unplanned failure serious enough to stop or degrade production. Structured capture — asset, symptom, root cause — turns breakdowns into a reliability improvement backlog.",
    whyItMatters: "Every unresolved breakdown will happen again.",
    relatedTerms: ["reactive-maintenance", "mtbf", "root-cause-analysis"],
    searchKeywords: ["breakdown", "machine failure"],
  }),
  e({
    slug: "spare-parts-management",
    term: "Spare Parts Management",
    category: "Maintenance & Reliability",
    shortDefinition: "Managing stock of maintenance parts to balance availability and cost.",
    definition: "Spare Parts Management balances the cost of holding parts against the cost of downtime waiting for them. Integrated with CMMS and reliability data, stocking decisions become data-driven.",
    whyItMatters: "Missing spares are a common hidden cause of long MTTR.",
    relatedTerms: ["cmms", "mttr", "mttf"],
    searchKeywords: ["spare parts"],
  }),
  e({
    slug: "lubrication-management",
    term: "Lubrication Management",
    category: "Maintenance & Reliability",
    shortDefinition: "Structured program controlling lubricant selection, application and monitoring.",
    definition: "Lubrication Management ensures the right lubricant is applied at the right interval, using clean handling and often monitored via oil analysis. Poor lubrication is a leading cause of premature failure.",
    whyItMatters: "Lubrication is one of the highest-ROI reliability practices, and one of the most neglected.",
    relatedTerms: ["preventive-maintenance", "condition-monitoring", "tpm"],
    searchKeywords: ["lubrication"],
  }),
  e({
    slug: "vibration-monitoring",
    term: "Vibration Monitoring",
    category: "Maintenance & Reliability",
    shortDefinition: "Measuring vibration to detect mechanical faults early.",
    definition: "Vibration Monitoring uses accelerometers to detect imbalance, misalignment, bearing wear and gear faults before they cause failure. It is a mainstay of condition-based and predictive maintenance for rotating equipment.",
    whyItMatters: "Vibration is often the earliest, cheapest signal that a machine is about to fail.",
    relatedTerms: ["condition-monitoring", "cbm", "predictive-maintenance"],
    searchKeywords: ["vibration monitoring"],
  }),
  e({
    slug: "thermography",
    term: "Thermography",
    category: "Maintenance & Reliability",
    shortDefinition: "Using infrared imaging to detect abnormal heat patterns.",
    definition: "Thermography uses infrared cameras to visualise heat, revealing loose electrical connections, motor overheating, insulation loss and process leaks. It is a low-cost, high-value condition monitoring technique.",
    whyItMatters: "Thermography catches electrical faults before they become fires or outages.",
    relatedTerms: ["condition-monitoring", "cbm", "predictive-maintenance"],
    searchKeywords: ["thermography", "infrared"],
  }),
  e({
    slug: "tpm",
    term: "TPM",
    fullName: "Total Productive Maintenance",
    acronym: "TPM",
    aliases: ["Total Productive Maintenance"],
    category: "Maintenance & Reliability",
    shortDefinition: "Program that makes reliability a shared responsibility of operations and maintenance.",
    definition: "TPM aims for zero breakdowns, zero defects and zero accidents by involving operators in basic care of their equipment (autonomous maintenance), while specialists focus on planned and improvement maintenance.",
    whyItMatters: "TPM is the cultural counterpart to OEE — measurement without engagement rarely improves.",
    relatedTerms: ["autonomous-maintenance", "oee", "kaizen"],
    searchKeywords: ["tpm"],
  }),
  e({
    slug: "autonomous-maintenance",
    term: "Autonomous Maintenance",
    category: "Maintenance & Reliability",
    shortDefinition: "Operators performing basic care of their own equipment.",
    definition: "Autonomous Maintenance is a TPM pillar in which operators clean, inspect, lubricate and tighten their machines using standard work. It catches deterioration early and frees maintenance for skilled work.",
    whyItMatters: "Operators are always the first to notice change — autonomous maintenance formalises that.",
    relatedTerms: ["tpm", "standard-work", "preventive-maintenance"],
    searchKeywords: ["autonomous maintenance"],
  }),
  e({
    slug: "rcm",
    term: "Reliability-Centered Maintenance",
    fullName: "Reliability-Centered Maintenance",
    acronym: "RCM",
    aliases: ["RCM"],
    category: "Maintenance & Reliability",
    shortDefinition: "Method for choosing the best maintenance strategy per failure mode.",
    definition: "RCM analyses each failure mode of a critical asset and selects the most cost-effective maintenance strategy — run-to-failure, time-based, condition-based or redesign. It rationalises overloaded PM programs.",
    whyItMatters: "RCM eliminates wasted PM effort while protecting truly critical failure modes.",
    relatedTerms: ["preventive-maintenance", "cbm", "fmea"],
    searchKeywords: ["rcm"],
  }),

  // ============ Planning & Traceability ============
  e({
    slug: "bom",
    term: "BOM",
    fullName: "Bill of Materials",
    acronym: "BOM",
    aliases: ["Bill of Materials"],
    category: "Planning & Traceability",
    shortDefinition: "Structured list of materials and quantities required to produce a product.",
    definition: "A Bill of Materials defines every component, sub-assembly and raw material needed for one unit of a finished product. Engineering, manufacturing and costing BOM variants may exist and must stay synchronised.",
    whyItMatters: "BOM accuracy is the foundation of MRP, costing and material traceability.",
    relatedTerms: ["routing", "mrp", "plm"],
    searchKeywords: ["bom", "bill of materials"],
  }),
  e({
    slug: "routing",
    term: "Routing",
    category: "Planning & Traceability",
    shortDefinition: "Defined sequence of operations and work centres to make a product.",
    definition: "A Routing specifies the operations, work centres, standard times and tooling required to convert materials into a finished product. It drives scheduling, costing and work-order execution.",
    whyItMatters: "Accurate routings turn theoretical capacity into a schedulable plan.",
    relatedTerms: ["bom", "work-order", "aps"],
    searchKeywords: ["routing"],
  }),
  e({
    slug: "work-order",
    term: "Work Order",
    aliases: ["Production Order", "Manufacturing Order"],
    category: "Planning & Traceability",
    shortDefinition: "Authorisation to produce a specific quantity of a product.",
    definition: "A Work Order (or Production Order / Manufacturing Order) instructs the shop floor to produce a given quantity of a product by a certain date, using specified materials, routings and quality checks. MES tracks its execution.",
    whyItMatters: "The work order is the unit of accountability between planning and execution.",
    relatedTerms: ["routing", "production-scheduling", "mes"],
    searchKeywords: ["work order", "production order"],
  }),
  e({
    slug: "batch",
    term: "Batch",
    aliases: ["Lot"],
    category: "Planning & Traceability",
    shortDefinition: "Quantity of product produced together under uniform conditions.",
    definition: "A Batch (or Lot) is a group of units produced under the same conditions and traceable as a single quality entity. Batches are the atomic unit of traceability in process, food, pharma and chemical industries.",
    whyItMatters: "Batch-level tracking is the smallest meaningful traceability unit in most process plants.",
    relatedTerms: ["lot", "batch-tracking", "genealogy"],
    searchKeywords: ["batch", "lot"],
  }),
  e({
    slug: "serial-number",
    term: "Serial Number",
    category: "Planning & Traceability",
    shortDefinition: "Unique identifier assigned to an individual unit.",
    definition: "A Serial Number uniquely identifies a single unit and enables unit-level traceability, warranty and recall workflows. It is issued at production and follows the unit for life.",
    whyItMatters: "Serial numbers make unit-level warranty and recall management feasible.",
    relatedTerms: ["serialization", "product-traceability", "genealogy"],
    searchKeywords: ["serial number"],
  }),
  e({
    slug: "production-scheduling",
    term: "Production Scheduling",
    category: "Planning & Traceability",
    shortDefinition: "Assigning work orders to resources over time.",
    definition: "Production Scheduling sequences and assigns work orders to specific machines, lines and shifts, respecting capacity, materials and changeover rules. It bridges MRP planning and MES execution.",
    whyItMatters: "Good scheduling protects both throughput and delivery promises.",
    relatedTerms: ["aps", "finite-capacity-scheduling", "dispatching"],
    searchKeywords: ["production scheduling"],
  }),
  e({
    slug: "finite-capacity-scheduling",
    term: "Finite Capacity Scheduling",
    category: "Planning & Traceability",
    shortDefinition: "Scheduling that respects real capacity constraints of each resource.",
    definition: "Finite Capacity Scheduling generates plans that never exceed the actual capacity of machines, tooling or people, unlike infinite-capacity MRP. It is a core capability of APS systems.",
    whyItMatters: "Only finite-capacity schedules are physically executable on the shop floor.",
    relatedTerms: ["aps", "production-scheduling"],
    searchKeywords: ["finite capacity"],
  }),
  e({
    slug: "dispatching",
    term: "Dispatching",
    category: "Planning & Traceability",
    shortDefinition: "Assigning specific work to operators and resources at execution time.",
    definition: "Dispatching is the just-in-time release of work orders and tasks to specific operators and stations, often driven by an MES dispatch board that reflects the current schedule and shop-floor state.",
    whyItMatters: "Digital dispatching removes the paper travellers and clipboard queues on the floor.",
    relatedTerms: ["production-scheduling", "mes", "work-order"],
    searchKeywords: ["dispatching"],
  }),
  e({
    slug: "material-consumption",
    term: "Material Consumption",
    category: "Planning & Traceability",
    shortDefinition: "Actual materials used during production, recorded against orders.",
    definition: "Material Consumption records the real quantities of raw materials and components consumed by each work order, distinguishing standard usage from waste and variance.",
    whyItMatters: "Consumption data is critical for accurate costing, inventory and BOM improvement.",
    relatedTerms: ["bom", "production-confirmation", "wip"],
    searchKeywords: ["material consumption"],
  }),
  e({
    slug: "production-confirmation",
    term: "Production Confirmation",
    category: "Planning & Traceability",
    shortDefinition: "Reporting completed output and consumption back to ERP.",
    definition: "Production Confirmation posts actual output, scrap and consumption against a work order, updating ERP inventory and financials. Automating it from MES eliminates end-of-shift paper-to-keyboard.",
    whyItMatters: "Automatic confirmations keep ERP inventory truthful in real time.",
    relatedTerms: ["erp", "material-consumption", "work-order"],
    searchKeywords: ["production confirmation"],
  }),
  e({
    slug: "production-tracking",
    term: "Production Tracking",
    category: "Planning & Traceability",
    shortDefinition: "Live visibility of order progress across the shop floor.",
    definition: "Production Tracking shows the current status of every open order — quantities produced, remaining, on hold — across work centres, so schedulers and customer service can react in real time.",
    whyItMatters: "Live tracking is what enables credible delivery promises.",
    relatedTerms: ["work-order", "mes", "otif"],
    searchKeywords: ["production tracking"],
  }),
  e({
    slug: "batch-tracking",
    term: "Batch Tracking",
    aliases: ["Lot Tracking"],
    category: "Planning & Traceability",
    shortDefinition: "Tracing batches through production, warehousing and shipment.",
    definition: "Batch Tracking (or Lot Tracking) records every location, transformation and quality event a batch experiences, supporting recalls, complaint investigations and regulatory reporting.",
    whyItMatters: "Without batch tracking, a recall becomes a full-inventory scrap event.",
    relatedTerms: ["batch", "genealogy", "product-traceability"],
    searchKeywords: ["batch tracking", "lot tracking"],
  }),
  e({
    slug: "inventory-accuracy",
    term: "Inventory Accuracy",
    category: "Planning & Traceability",
    shortDefinition: "Degree to which recorded inventory matches physical stock.",
    definition: "Inventory Accuracy compares system-recorded stock against physical count. Low accuracy breaks MRP, causes stockouts and forces expensive safety stock.",
    whyItMatters: "Inventory accuracy above 98% is often a prerequisite for any Lean or Just-in-Time initiative.",
    relatedTerms: ["wms", "erp", "material-consumption"],
    searchKeywords: ["inventory accuracy"],
  }),
  e({
    slug: "production-lead-time",
    term: "Production Lead Time",
    category: "Planning & Traceability",
    shortDefinition: "Elapsed time to complete a work order from release to finish.",
    definition: "Production Lead Time is the total time from work-order release to finished-goods completion, spanning queue, setup, run and move times. Shortening it improves flexibility and cash conversion.",
    whyItMatters: "Shorter production lead time is one of the most valuable outputs of a mature MES program.",
    relatedTerms: ["lead-time", "wip", "cycle-time"],
    searchKeywords: ["production lead time"],
  }),
  e({
    slug: "otif",
    term: "OTIF",
    fullName: "On Time In Full",
    acronym: "OTIF",
    aliases: ["Order Fulfilment", "On Time In Full"],
    category: "Planning & Traceability",
    shortDefinition: "Share of customer orders delivered on time and in full quantity.",
    definition: "OTIF measures the proportion of customer orders delivered on the promised date with the promised quantity. It is a headline customer-service KPI that consolidates planning, execution and logistics performance.",
    whyItMatters: "OTIF is what customers actually feel — and what strategic accounts increasingly scorecard.",
    relatedTerms: ["schedule-attainment", "production-lead-time", "available-to-promise"],
    searchKeywords: ["otif", "on time in full"],
  }),
  e({
    slug: "available-to-promise",
    term: "Available to Promise",
    category: "Planning & Traceability",
    shortDefinition: "Quantity of a product that can be committed to a new customer order.",
    definition: "Available to Promise (ATP) calculates uncommitted supply — current stock plus scheduled production minus committed orders — so sales can quote realistic delivery dates in real time.",
    whyItMatters: "Reliable ATP prevents both lost sales and broken promises.",
    relatedTerms: ["otif", "master-production-schedule", "erp"],
    searchKeywords: ["atp", "available to promise"],
  }),
  e({
    slug: "production-plan",
    term: "Production Plan",
    category: "Planning & Traceability",
    shortDefinition: "Aggregate plan of what a plant intends to produce over a period.",
    definition: "The Production Plan aligns production volumes with demand forecasts, inventory targets and capacity, usually at family or SKU-group level and monthly buckets. It is the input to the Master Production Schedule.",
    whyItMatters: "The production plan converts business strategy into an operations commitment.",
    relatedTerms: ["master-production-schedule", "mrp", "erp"],
    searchKeywords: ["production plan"],
  }),
  e({
    slug: "master-production-schedule",
    term: "Master Production Schedule",
    aliases: ["MPS"],
    category: "Planning & Traceability",
    shortDefinition: "Time-phased plan of specific end-products to produce.",
    definition: "The Master Production Schedule (MPS) breaks the aggregate production plan into specific SKUs and time buckets (usually weekly), driving MRP and detailed scheduling downstream.",
    whyItMatters: "A stable MPS is what keeps supply chain and production in sync week after week.",
    relatedTerms: ["production-plan", "mrp", "aps"],
    searchKeywords: ["mps", "master production schedule"],
  }),

  // ============ Lean Manufacturing ============
  e({
    slug: "lean-manufacturing",
    term: "Lean Manufacturing",
    category: "Lean Manufacturing",
    shortDefinition: "Production philosophy focused on maximising customer value with minimum waste.",
    definition: "Lean Manufacturing originates in the Toyota Production System and aims to deliver customer value with the least waste of time, materials and effort. Its tools — Kanban, 5S, SMED, VSM, Kaizen — depend on trustworthy real-time data to work at scale.",
    whyItMatters: "Lean without live data devolves into slogans; live data without Lean devolves into dashboards nobody uses.",
    relatedTerms: ["kaizen", "muda", "value-stream-mapping", "just-in-time"],
    searchKeywords: ["lean manufacturing", "tps"],
  }),
  e({
    slug: "kaizen",
    term: "Kaizen",
    category: "Lean Manufacturing",
    shortDefinition: "Continuous, incremental improvement driven by shop-floor teams.",
    definition: "Kaizen is the Lean practice of small, continuous improvements involving everyone from operators to managers. Real-time MES/OEE data turns Kaizen from opinion-based to fact-based — teams see the impact of each change within a shift.",
    whyItMatters: "Kaizen is the compounding engine behind every world-class factory.",
    relatedTerms: ["continuous-improvement", "oee", "5-whys"],
    searchKeywords: ["kaizen"],
  }),
  e({
    slug: "5s",
    term: "5S",
    category: "Lean Manufacturing",
    shortDefinition: "Workplace-organisation method: Sort, Set in order, Shine, Standardise, Sustain.",
    definition: "5S structures workplaces so tools, materials and information are where they should be. It is often the first Lean practice deployed because it makes deviations immediately visible.",
    whyItMatters: "5S is the physical prerequisite for reliable standard work and quick problem detection.",
    relatedTerms: ["standard-work", "visual-management", "kaizen"],
    searchKeywords: ["5s"],
  }),
  e({
    slug: "kanban",
    term: "Kanban",
    category: "Lean Manufacturing",
    shortDefinition: "Signal-based pull system that limits WIP.",
    definition: "Kanban uses cards, containers or electronic signals to authorise material movement or production only when downstream demand exists. It caps WIP and exposes flow problems.",
    whyItMatters: "Kanban is the practical mechanism that makes Just-in-Time work.",
    relatedTerms: ["pull-production", "just-in-time", "wip"],
    searchKeywords: ["kanban"],
  }),
  e({
    slug: "just-in-time",
    term: "Just in Time",
    fullName: "Just in Time",
    acronym: "JIT",
    aliases: ["JIT"],
    category: "Lean Manufacturing",
    shortDefinition: "Producing only what is needed, when it is needed, in the quantity needed.",
    definition: "Just-in-Time (JIT) manufacturing minimises inventory by synchronising production with actual demand. It requires short changeovers, reliable equipment and disciplined suppliers.",
    whyItMatters: "JIT liberates working capital and exposes hidden quality and reliability issues.",
    relatedTerms: ["kanban", "pull-production", "lean-manufacturing"],
    searchKeywords: ["jit", "just in time"],
  }),
  e({
    slug: "value-stream-mapping",
    term: "Value Stream Mapping",
    fullName: "Value Stream Mapping",
    acronym: "VSM",
    aliases: ["VSM"],
    category: "Lean Manufacturing",
    shortDefinition: "Visual technique that maps material and information flow through a process.",
    definition: "Value Stream Mapping (VSM) draws current-state material and information flow, quantifies lead time and value-added time, and then designs a future state with less waste. Real MES data makes VSMs accurate rather than aspirational.",
    whyItMatters: "VSM prevents Lean projects from optimising local steps that do not improve overall flow.",
    relatedTerms: ["lean-manufacturing", "muda", "lead-time"],
    searchKeywords: ["vsm", "value stream"],
  }),
  e({
    slug: "gemba",
    term: "Gemba",
    category: "Lean Manufacturing",
    shortDefinition: "The actual place where work is done — where problems must be understood.",
    definition: "Gemba means 'the real place' in Japanese. Gemba walks bring leaders to the shop floor to observe work, ask questions and support improvement rather than manage from reports.",
    whyItMatters: "Live shop-floor dashboards make gemba walks fact-based and specific.",
    relatedTerms: ["shop-floor-management", "visual-management", "kaizen"],
    searchKeywords: ["gemba"],
  }),
  e({
    slug: "andon",
    term: "Andon",
    category: "Lean Manufacturing",
    shortDefinition: "Visual signalling system that lets operators call for help in real time.",
    definition: "Andon originated in Toyota Production System and lets an operator flag a problem — quality, material, equipment — so support teams respond immediately. Digital Andon replaces physical lights with tablets and dashboards, logging every call for later Pareto analysis.",
    whyItMatters: "Andon converts silent problems into visible ones the team is expected to solve.",
    relatedTerms: ["jidoka", "visual-management", "shop-floor-management"],
    searchKeywords: ["andon"],
  }),
  e({
    slug: "jidoka",
    term: "Jidoka",
    category: "Lean Manufacturing",
    shortDefinition: "Automation with a human touch — machines stop when abnormalities occur.",
    definition: "Jidoka is the Lean principle of building intelligence into equipment so it stops automatically on abnormalities, preventing defects and calling for human attention. It underpins Andon and modern automation.",
    whyItMatters: "Jidoka contains problems at the source rather than propagating defects.",
    relatedTerms: ["andon", "poka-yoke", "lean-manufacturing"],
    searchKeywords: ["jidoka"],
  }),
  e({
    slug: "heijunka",
    term: "Heijunka",
    category: "Lean Manufacturing",
    shortDefinition: "Levelling production by volume and mix to smooth flow.",
    definition: "Heijunka levels production so that different products are made in small, repeating patterns instead of large batches, reducing WIP and improving responsiveness to demand changes.",
    whyItMatters: "Heijunka is what makes Just-in-Time robust to demand variability.",
    relatedTerms: ["just-in-time", "kanban", "lean-manufacturing"],
    searchKeywords: ["heijunka"],
  }),
  e({
    slug: "smed",
    term: "SMED",
    aliases: ["Single-Minute Exchange of Die"],
    category: "Lean Manufacturing",
    shortDefinition: "Methodology for reducing changeover times to under 10 minutes.",
    definition: "SMED (Single-Minute Exchange of Die) separates internal from external setup steps and systematically converts internal to external, targeting single-digit-minute changeovers. It is the primary lever for reducing Availability loss on high-mix lines.",
    whyItMatters: "SMED enables smaller batches, less WIP and faster response to demand.",
    relatedTerms: ["changeover-time", "setup-time", "lean-manufacturing"],
    searchKeywords: ["smed"],
  }),
  e({
    slug: "standard-work",
    term: "Standard Work",
    category: "Lean Manufacturing",
    shortDefinition: "Documented best-known method of performing a task.",
    definition: "Standard Work is the current best-known way to perform an operation, defined in enough detail to be repeated safely and consistently. It is the baseline every Kaizen improves upon.",
    whyItMatters: "Without standard work, improvements cannot be measured or sustained.",
    relatedTerms: ["5s", "kaizen", "autonomous-maintenance"],
    searchKeywords: ["standard work"],
  }),
  e({
    slug: "continuous-improvement",
    term: "Continuous Improvement",
    category: "Lean Manufacturing",
    shortDefinition: "Ongoing effort to improve products, services and processes.",
    definition: "Continuous Improvement (CI) is the systematic pursuit of incremental and breakthrough improvements, using methods such as Kaizen, PDCA and Six Sigma, supported by reliable measurement.",
    whyItMatters: "CI compounds — a plant that improves 1% per month leaves peers behind within a year.",
    relatedTerms: ["kaizen", "lean-manufacturing", "six-sigma"],
    searchKeywords: ["continuous improvement", "ci"],
  }),
  e({
    slug: "muda",
    term: "Muda",
    aliases: ["Waste"],
    category: "Lean Manufacturing",
    shortDefinition: "Japanese term for waste — activity that consumes resources without adding value.",
    definition: "Muda is any activity that does not add value from the customer's perspective. The seven classical wastes are overproduction, waiting, transport, over-processing, inventory, motion and defects; an eighth (unused talent) is often added.",
    whyItMatters: "Naming waste is the first step to systematically removing it.",
    relatedTerms: ["mura", "muri", "lean-manufacturing"],
    searchKeywords: ["muda", "waste"],
  }),
  e({
    slug: "mura",
    term: "Mura",
    category: "Lean Manufacturing",
    shortDefinition: "Unevenness or variability in workflow.",
    definition: "Mura is variability — in demand, workload or process output — that creates peaks, idle time and quality problems. Heijunka and SMED are common counters.",
    whyItMatters: "Reducing Mura often eliminates the need to over-resource for peaks.",
    relatedTerms: ["muda", "muri", "heijunka"],
    searchKeywords: ["mura"],
  }),
  e({
    slug: "muri",
    term: "Muri",
    category: "Lean Manufacturing",
    shortDefinition: "Overburden of people or equipment.",
    definition: "Muri is overburdening people or equipment beyond sustainable capacity, causing quality, safety and reliability failures. It is often caused by Mura upstream.",
    whyItMatters: "Chronic overburden destroys the very resources you rely on for output.",
    relatedTerms: ["muda", "mura", "lean-manufacturing"],
    searchKeywords: ["muri", "overburden"],
  }),
  e({
    slug: "six-sigma",
    term: "Six Sigma",
    category: "Lean Manufacturing",
    shortDefinition: "Data-driven methodology for reducing process variation and defects.",
    definition: "Six Sigma uses statistical techniques within a structured DMAIC framework to reduce variation and defects to fewer than 3.4 per million opportunities. It is often combined with Lean as 'Lean Six Sigma'.",
    whyItMatters: "Six Sigma delivers repeatable, statistically valid results on complex quality problems.",
    relatedTerms: ["dmaic", "spc", "continuous-improvement"],
    searchKeywords: ["six sigma"],
  }),
  e({
    slug: "dmaic",
    term: "DMAIC",
    category: "Lean Manufacturing",
    shortDefinition: "Six Sigma project framework: Define, Measure, Analyze, Improve, Control.",
    definition: "DMAIC is the standard Six Sigma project cycle. Each phase has deliverables and gates, ending in a Control plan that sustains improvements — typically via SPC and standard work.",
    whyItMatters: "DMAIC prevents Six Sigma projects from stopping at analysis without behavioural change.",
    relatedTerms: ["six-sigma", "spc", "continuous-improvement"],
    searchKeywords: ["dmaic"],
  }),
  e({
    slug: "visual-management",
    term: "Visual Management",
    category: "Lean Manufacturing",
    shortDefinition: "Making status, standards and problems visible at a glance.",
    definition: "Visual Management uses boards, colour codes, floor markings, Andon lights and live dashboards so anyone entering the area understands current status in seconds. It shortens response time and reinforces standards.",
    whyItMatters: "If you can't see abnormal at a glance, you will react too slowly.",
    relatedTerms: ["andon", "5s", "shop-floor-management"],
    searchKeywords: ["visual management"],
  }),
  e({
    slug: "pull-production",
    term: "Pull Production",
    category: "Lean Manufacturing",
    shortDefinition: "Producing only in response to actual downstream demand.",
    definition: "Pull Production makes downstream consumption the trigger for upstream production, typically via Kanban. It caps WIP and stops overproduction, the worst of the seven wastes.",
    whyItMatters: "Pull is what actually reduces inventory and lead time, not just aspiration.",
    relatedTerms: ["push-production", "kanban", "just-in-time"],
    searchKeywords: ["pull production"],
  }),
  e({
    slug: "push-production",
    term: "Push Production",
    category: "Lean Manufacturing",
    shortDefinition: "Producing based on forecasts and schedules, regardless of downstream demand.",
    definition: "Push Production runs work through the plant according to a plan, pushing output downstream whether or not it is needed. It is efficient at the station level but tends to build WIP and long lead times.",
    whyItMatters: "Push is often necessary in high-volume plants but must be balanced with buffers and levelling.",
    relatedTerms: ["pull-production", "mrp", "wip"],
    searchKeywords: ["push production"],
  }),
  e({
    slug: "one-piece-flow",
    term: "One-Piece Flow",
    category: "Lean Manufacturing",
    shortDefinition: "Processing units one at a time through connected steps.",
    definition: "One-Piece Flow moves single units through sequential operations without batching. It dramatically shortens lead time, exposes quality problems immediately and reduces WIP.",
    whyItMatters: "One-piece flow is the fastest way to see defects — and thus to eliminate them.",
    relatedTerms: ["pull-production", "takt-time", "lean-manufacturing"],
    searchKeywords: ["one piece flow"],
  }),

  // ============ Industrial Hardware ============
  e({
    slug: "industrial-tablet",
    term: "Industrial Tablet",
    aliases: ["Rugged Tablet"],
    category: "Industrial Hardware",
    shortDefinition: "Ruggedised tablet built for shop-floor and outdoor use.",
    definition: "An industrial (or rugged) tablet is engineered for drops, dust, moisture and temperature extremes. It runs operator apps such as digital work instructions, quality checks and material transactions right at the point of use.",
    whyItMatters: "Consumer tablets rarely survive a year on a real production floor.",
    relatedTerms: ["mobile-computer", "hmi-panel", "operator-terminal"],
    searchKeywords: ["industrial tablet", "rugged tablet"],
  }),
  e({
    slug: "mobile-computer",
    term: "Mobile Computer",
    aliases: ["Handheld Terminal"],
    category: "Industrial Hardware",
    shortDefinition: "Handheld device combining computing, scanning and wireless connectivity.",
    definition: "A mobile computer or handheld terminal integrates a rugged handset with barcode scanning, keypad or touch and wireless networking, used for inventory, picking and shop-floor transactions.",
    whyItMatters: "Handhelds turn every warehouse and shop-floor move into a real-time system event.",
    relatedTerms: ["barcode-scanner", "wms", "industrial-tablet"],
    searchKeywords: ["mobile computer", "handheld terminal"],
  }),
  e({
    slug: "barcode-scanner",
    term: "Barcode Scanner",
    category: "Industrial Hardware",
    shortDefinition: "Device that reads 1D or 2D barcodes.",
    definition: "A barcode scanner decodes printed codes into machine-readable identifiers. Industrial scanners handle scratched labels, direct-part marks and difficult angles.",
    whyItMatters: "Reliable scanning is what makes traceability and material transactions frictionless.",
    relatedTerms: ["wireless-barcode-scanner", "mobile-computer", "product-traceability"],
    searchKeywords: ["barcode scanner"],
  }),
  e({
    slug: "wireless-barcode-scanner",
    term: "Wireless Barcode Scanner",
    category: "Industrial Hardware",
    shortDefinition: "Barcode scanner that connects wirelessly to a host device.",
    definition: "A wireless barcode scanner (Bluetooth or Wi-Fi) removes the cable between operator and host, enabling scanning at pallets, benches or overhead locations without moving the terminal.",
    whyItMatters: "Cable-free scanning eliminates ergonomic and safety issues in busy shop-floor zones.",
    relatedTerms: ["barcode-scanner", "mobile-computer"],
    searchKeywords: ["wireless barcode scanner"],
  }),
  e({
    slug: "rfid",
    term: "RFID",
    category: "Industrial Hardware",
    shortDefinition: "Radio-frequency identification for reading tags without line-of-sight.",
    definition: "RFID uses radio-frequency tags read by antennas at short or long range without line-of-sight. It suits high-throughput identification of pallets, containers, tools and assets.",
    whyItMatters: "RFID enables identification workflows barcodes cannot support at scale.",
    relatedTerms: ["rfid-reader", "rfid-tag", "product-traceability"],
    searchKeywords: ["rfid"],
  }),
  e({
    slug: "rfid-reader",
    term: "RFID Reader",
    category: "Industrial Hardware",
    shortDefinition: "Device that reads (and often writes) RFID tags.",
    definition: "An RFID reader emits an RF field and processes responses from tags, decoding their unique IDs and, in some cases, additional stored data. Readers exist in handheld, sled, stationary and gate form factors.",
    whyItMatters: "Reader placement and form factor make or break an RFID deployment.",
    relatedTerms: ["rfid", "rfid-tag"],
    searchKeywords: ["rfid reader"],
  }),
  e({
    slug: "rfid-tag",
    term: "RFID Tag",
    category: "Industrial Hardware",
    shortDefinition: "Small electronic label that responds to an RFID reader.",
    definition: "An RFID tag contains a chip and antenna that respond to a reader with a unique ID. Tags come in passive (no battery), active (battery-powered) and semi-passive variants, in many form factors for pallets, tools and rugged assets.",
    whyItMatters: "Right-fit tags survive the environment and stay readable for the life of the asset.",
    relatedTerms: ["rfid", "rfid-reader"],
    searchKeywords: ["rfid tag"],
  }),
  e({
    slug: "industrial-label-printer",
    term: "Industrial Label Printer",
    category: "Industrial Hardware",
    shortDefinition: "Ruggedised printer for producing durable barcode and identification labels.",
    definition: "An industrial label printer produces high-volume, durable labels for cartons, pallets and products, typically using thermal-transfer technology and integrated with WMS/MES to print on demand.",
    whyItMatters: "Label quality directly determines downstream scan success and traceability integrity.",
    relatedTerms: ["thermal-printer", "barcode-scanner", "wms"],
    searchKeywords: ["label printer"],
  }),
  e({
    slug: "thermal-printer",
    term: "Thermal Printer",
    category: "Industrial Hardware",
    shortDefinition: "Printer that uses heat to produce labels or receipts.",
    definition: "Thermal printers use direct-thermal or thermal-transfer technology to produce labels quickly and reliably. Thermal-transfer versions produce longer-lasting labels suited to harsh environments.",
    whyItMatters: "Choosing between direct-thermal and thermal-transfer directly affects label lifetime and cost.",
    relatedTerms: ["industrial-label-printer"],
    searchKeywords: ["thermal printer"],
  }),
  e({
    slug: "edge-controller",
    term: "Edge Controller",
    category: "Industrial Hardware",
    shortDefinition: "Compact controller running control and connectivity logic at the machine.",
    definition: "An edge controller combines PLC-style deterministic control with IIoT connectivity and edge computing in one device, streamlining architectures for smart machines and retrofits.",
    whyItMatters: "Edge controllers cut the number of boxes and networks required for connected machines.",
    relatedTerms: ["edge-device", "plc", "industrial-gateway"],
    searchKeywords: ["edge controller"],
  }),
  e({
    slug: "current-transformer",
    term: "Current Transformer",
    aliases: ["CT"],
    category: "Industrial Hardware",
    shortDefinition: "Sensor that measures alternating current by induction.",
    definition: "A Current Transformer (CT) is clamped around a conductor to measure AC current for metering, protection or monitoring. Split-core CTs allow installation without disconnecting the circuit.",
    whyItMatters: "CTs are the workhorse sensor of energy monitoring — chosen and installed correctly, they scale energy visibility cheaply.",
    relatedTerms: ["flexible-current-transformer", "smart-energy-meter", "energy-metering"],
    searchKeywords: ["current transformer", "ct"],
  }),
  e({
    slug: "flexible-current-transformer",
    term: "Flexible Current Transformer",
    aliases: ["Rogowski Coil"],
    category: "Industrial Hardware",
    shortDefinition: "Flexible-loop current sensor that fits around large or awkward conductors.",
    definition: "A flexible CT (often a Rogowski coil) uses a flexible loop that can be threaded around large or bundled conductors. It is ideal for retrofit metering in busy switchboards.",
    whyItMatters: "Flexible CTs make energy monitoring possible where rigid CTs simply do not fit.",
    relatedTerms: ["current-transformer", "smart-energy-meter"],
    searchKeywords: ["flexible ct", "rogowski"],
  }),
  e({
    slug: "smart-energy-meter",
    term: "Smart Energy Meter",
    category: "Industrial Hardware",
    shortDefinition: "Electricity meter with digital communication and detailed measurements.",
    definition: "A smart energy meter measures voltage, current, power, energy, power factor and harmonics and exposes the data via Modbus, MQTT or similar protocols for integration with EnMS platforms.",
    whyItMatters: "Smart meters make sub-metering, load-profile and EnPI analysis practical at scale.",
    relatedTerms: ["smart-meter", "current-transformer", "energy-monitoring"],
    searchKeywords: ["smart energy meter"],
  }),
  e({
    slug: "industrial-sensor",
    term: "Industrial Sensor",
    aliases: ["Machine-Mounted Sensor"],
    category: "Industrial Hardware",
    shortDefinition: "Ruggedised sensor designed for industrial environments.",
    definition: "An industrial sensor withstands vibration, dust, moisture, temperature and EMI while measuring temperature, vibration, current, distance or pressure. Machine-mounted variants attach directly to production equipment for monitoring and control.",
    whyItMatters: "Only industrial-grade sensors deliver reliable data in real factory conditions.",
    relatedTerms: ["sensor", "smart-sensor", "condition-monitoring"],
    searchKeywords: ["industrial sensor"],
  }),
  e({
    slug: "hmi-panel",
    term: "HMI Panel",
    aliases: ["Operator Terminal"],
    category: "Industrial Hardware",
    shortDefinition: "Fixed-mount operator screen for machine or line control.",
    definition: "An HMI Panel or Operator Terminal is a ruggedised touchscreen mounted on or near a machine, presenting machine state and controls to operators and often serving as a data-entry point for MES.",
    whyItMatters: "Operator terminals are where MES meets the person running the machine.",
    relatedTerms: ["hmi", "industrial-tablet", "plc"],
    searchKeywords: ["hmi panel", "operator terminal"],
  }),
];

/** Resolve a slug or acronym/full-name alias to the canonical entry. */
export const getGlossaryEntry = (slugOrAlias: string): GlossaryEntry | undefined => {
  const key = slugOrAlias.toLowerCase();
  const bySlug = glossary.find((g) => g.slug === key);
  if (bySlug) return bySlug;
  return glossary.find(
    (g) =>
      g.slug.replace(/-/g, "") === key.replace(/-/g, "") ||
      g.aliases?.some((a) => a.toLowerCase() === slugOrAlias.toLowerCase()) ||
      g.term.toLowerCase() === slugOrAlias.toLowerCase() ||
      (g.fullName && g.fullName.toLowerCase() === slugOrAlias.toLowerCase()),
  );
};

export const glossaryCount = glossary.length;

/** Sort helper: numeric-leading terms (5S, 5 Whys, 8D) come before A. */
export const compareGlossaryTerms = (a: GlossaryEntry, b: GlossaryEntry) => {
  const an = /^\d/.test(a.term);
  const bn = /^\d/.test(b.term);
  if (an !== bn) return an ? -1 : 1;
  return a.term.localeCompare(b.term, undefined, { sensitivity: "base", numeric: true });
};